import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Typography, Box, Button, MenuItem, Select, FormControl, Paper, TextField, CircularProgress, Alert, List, ListItem, ListItemText, Stack, Snackbar, Alert as MuiAlert, Autocomplete, useMediaQuery, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Chip, Badge, Card, CardContent, LinearProgress, ToggleButtonGroup, ToggleButton, Collapse } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip as ChartTooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { Email, Category, Edit, Save, Cancel, DeleteForever, Person, Business, ExpandMore, ExpandLess } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import tracker from './activityTracker';
import { typedTransactionsSheet, typedTopEntitiesSheet } from './utils/excelAnalysisExport';
import { getCategoryFromKeywordRules } from './utils/keywordCategoryMatch';
import { getUploadErrorMessage } from './utils/uploadPdfErrors';
import { ANALYZER_MAX_PAGES_PER_FILE } from './uploadHelpText';
import MobileAppCta from './MobileAppCta';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend, ArcElement);

// Define a diverse color palette for up to 10 entities
const COLORS = [
  '#1976d2', // Blue
  '#e91e63', // Pink
  '#ff9800', // Orange
  '#4caf50', // Green
  '#9c27b0', // Purple
  '#f44336', // Red
  '#00bcd4', // Cyan
  '#ffeb3b', // Yellow
  '#795548', // Brown
  '#607d8b'  // Blue Grey
];

// Define predefined categories with colors
// Personal view — expense categories
const PERSONAL_EXPENSE_CATEGORIES = [
  { name: 'Food & Dining', color: '#ff9800', icon: '🍽️' },
  { name: 'Transportation', color: '#2196f3', icon: '🚗' },
  { name: 'Shopping', color: '#e91e63', icon: '🛍️' },
  { name: 'Entertainment', color: '#9c27b0', icon: '🎬' },
  { name: 'Rent and Utilities', color: '#607d8b', icon: '🏠' },
  { name: 'Healthcare', color: '#f44336', icon: '🏥' },
  { name: 'Education', color: '#4caf50', icon: '📚' },
  { name: 'Travel', color: '#00bcd4', icon: '✈️' },
  { name: 'Personal Care', color: '#ffeb3b', icon: '💅' },
  { name: 'Groceries', color: '#8bc34a', icon: '🛒' },
  { name: 'Family', color: '#ff5722', icon: '👨‍👩‍👧‍👦' },
  { name: 'Savings', color: '#9c27b0', icon: '💰' },
  { name: 'Gifts', color: '#3f51b5', icon: '🎁' },
  { name: 'Withdrawal Charges', color: '#009688', icon: '💸' },
  { name: 'Transaction Charges', color: '#673ab7', icon: '💳' },
  { name: 'Airtime Purchase', color: '#cddc39', icon: '📱' },
  { name: 'Other', color: '#9e9e9e', icon: '📌' }
];

// Personal view — income categories (paid in)
const PERSONAL_INCOME_CATEGORIES = [
  { name: 'Salary', color: '#4caf50', icon: '💼' },
  { name: 'Freelance Income', color: '#8bc34a', icon: '💻' },
  { name: 'Family Remittance', color: '#ff5722', icon: '👨‍👩‍👧‍👦' },
  { name: 'Gifts Received', color: '#3f51b5', icon: '🎁' },
  { name: 'Refunds', color: '#00bcd4', icon: '↩️' },
  { name: 'Interest & Dividends', color: '#9c27b0', icon: '📈' },
  { name: 'Rental Income', color: '#607d8b', icon: '🏠' },
  { name: 'Business Sales', color: '#ff9800', icon: '🏪' },
  { name: 'Government Benefits', color: '#2196f3', icon: '🏛️' },
  { name: 'Loan Disbursement', color: '#673ab7', icon: '🏦' },
  { name: 'Savings Withdrawal', color: '#795548', icon: '💰' },
  { name: 'Side Hustle', color: '#e91e63', icon: '🛠️' },
  { name: 'Other Income', color: '#9e9e9e', icon: '📌' }
];

// Business view — expense categories
const BUSINESS_EXPENSE_CATEGORIES = [
  { name: 'Payroll', color: '#f44336', icon: '👥' },
  { name: 'Rent & Utilities', color: '#607d8b', icon: '🏢' },
  { name: 'Inventory & Stock', color: '#ff9800', icon: '📦' },
  { name: 'Marketing & Ads', color: '#e91e63', icon: '📢' },
  { name: 'Supplies & Materials', color: '#795548', icon: '🧰' },
  { name: 'Professional Services', color: '#3f51b5', icon: '⚖️' },
  { name: 'Tax & Compliance', color: '#9c27b0', icon: '🏛️' },
  { name: 'Insurance', color: '#00bcd4', icon: '🛡️' },
  { name: 'Equipment & Assets', color: '#ff5722', icon: '🖥️' },
  { name: 'Loans & Interest', color: '#673ab7', icon: '🏦' },
  { name: 'Transport & Logistics', color: '#2196f3', icon: '🚚' },
  { name: 'Subscriptions & Software', color: '#009688', icon: '💻' },
  { name: 'Travel & Entertainment', color: '#cddc39', icon: '✈️' },
  { name: 'Transaction Charges', color: '#ffc107', icon: '💳' },
  { name: 'Miscellaneous', color: '#9e9e9e', icon: '📌' }
];

// Business view — income categories (paid in)
const BUSINESS_INCOME_CATEGORIES = [
  { name: 'Revenue', color: '#4caf50', icon: '💵' },
  { name: 'Client Payments', color: '#8bc34a', icon: '🤝' },
  { name: 'Grants & Funding', color: '#3f51b5', icon: '🏛️' },
  { name: 'Loan Disbursement', color: '#673ab7', icon: '🏦' },
  { name: 'Investment Returns', color: '#9c27b0', icon: '📈' },
  { name: 'Refunds & Rebates', color: '#00bcd4', icon: '↩️' },
  { name: 'Asset Sales', color: '#ff5722', icon: '🏷️' },
  { name: 'Commission', color: '#ff9800', icon: '🤑' },
  { name: 'Rental Income', color: '#607d8b', icon: '🏢' },
  { name: 'Other Income', color: '#9e9e9e', icon: '📌' }
];

const ALL_CATEGORIES = [
  ...PERSONAL_EXPENSE_CATEGORIES,
  ...PERSONAL_INCOME_CATEGORIES,
  ...BUSINESS_EXPENSE_CATEGORIES,
  ...BUSINESS_INCOME_CATEGORIES,
].filter((cat, idx, arr) => arr.findIndex(c => c.name === cat.name) === idx);

// Keyword-based auto-tagging rules. Ordered by priority (first match wins).
// Expense keywords
const KEYWORD_CATEGORY_RULES = [
  { category: 'Shopping', keywords: ['supermarket', 'mart', 'shop', 'naivas', 'carrefour', 'magunas', 'jumia', 'kilimall', 'masoko', 'quickmart', 'cleanshelf', 'tuskys', 'uchumi', 'shoprite', 'game', 'mall', 'kiosk'] },
  { category: 'Food & Dining', keywords: ['restaurant', 'hotel', 'cafe', 'coffee', 'pizza', 'chicken', 'kfc', 'java', 'artcaffe', 'subway', 'dominos', 'burger', 'bakery', 'food', 'catering', 'dine', 'kitchen', 'meals'] },
  { category: 'Groceries', keywords: ['greengrocer', 'grocer', 'vegetables', 'fruits', 'market', 'butcher', 'butchery', 'fresh', 'farm', 'dairy', 'maziwa'] },
  { category: 'Transportation', keywords: ['uber', 'bolt', 'taxi', 'matatu', 'sgr', 'fare', 'parking', 'fuel', 'petrol', 'shell', 'total energies', 'rubis', 'kobil', 'gulf', 'galana', 'gas station', 'stage', 'boda', 'pikipiki'] },
  { category: 'Rent and Utilities', keywords: ['rent', 'kplc', 'kenya power', 'safaricom home', 'zuku', 'faiba', 'startimes', 'dstv', 'gotv', 'water', 'sewerage', 'garbage', 'electricity', 'wifi', 'internet', 'airbnb'] },
  { category: 'Healthcare', keywords: ['hospital', 'clinic', 'pharmacy', 'chemist', 'medical', 'doctor', 'dental', 'lab', 'diagnostic', 'health', 'optical', 'ambulance', 'nhif'] },
  { category: 'Education', keywords: ['school', 'university', 'college', 'academy', 'tuition', 'fees', 'books', 'stationery', 'exam', 'helb', 'student'] },
  { category: 'Airtime Purchase', keywords: ['airtime', 'data bundle', 'safaricom', 'airtel', 'telkom', 'bundles', 'top up', 'topup'] },
  { category: 'Transaction Charges', keywords: ['paybill charge', 'transaction charge'] },
  { category: 'Withdrawal Charges', keywords: ['withdrawal charge'] },
  { category: 'Personal Care', keywords: ['salon', 'barber', 'spa', 'beauty', 'gym', 'fitness', 'cosmetic'] },
  { category: 'Savings', keywords: ['sacco', 'chama', 'investment', 'deposit', 'mshwari', 'kcb mpesa', 'lock savings', 'mali'] },
  { category: 'Entertainment', keywords: ['cinema', 'movie', 'netflix', 'showmax', 'spotify', 'gaming', 'bet', 'sportpesa', 'betika', 'mozzart', 'odibets', 'betway', 'casino', 'lotto', 'concert', 'ticket'] },
  { category: 'Gifts', keywords: ['gift', 'donation', 'harambee', 'fundrais', 'offering', 'tithe', 'church', 'mosque', 'charity'] },
  { category: 'Family', keywords: ['pocket money', 'upkeep', 'maintenance'] },
  { category: 'Travel', keywords: ['flight', 'airline', 'kenya airways', 'jambojet', 'booking', 'trip', 'safari', 'tour', 'visa', 'passport'] },
  // Income keywords
  { category: 'Salary', keywords: ['salary', 'payroll', 'wages', 'stipend', 'allowance'] },
  { category: 'Family Remittance', keywords: ['remittance', 'diaspora', 'western union', 'worldremit', 'sendwave'] },
  { category: 'Freelance Income', keywords: ['freelance', 'consultancy', 'contract', 'gig', 'upwork', 'fiverr'] },
  { category: 'Business Sales', keywords: ['sales', 'revenue', 'payment received', 'client payment', 'invoice'] },
  { category: 'Refunds', keywords: ['refund', 'reversal', 'cashback', 'reimbursement'] },
  { category: 'Loan Disbursement', keywords: ['loan', 'tala', 'branch', 'zenka', 'disbursement'] },
  { category: 'Interest & Dividends', keywords: ['interest', 'dividend', 'yield', 'maturity', 'treasury'] },
  { category: 'Government Benefits', keywords: ['government', 'county', 'nssf', 'nhif benefit'] },
];

const getKeywordCategory = (entityName) =>
  getCategoryFromKeywordRules(entityName, KEYWORD_CATEGORY_RULES);

const CUSTOM_CATEGORY_COLORS = [
  '#ef6c00', '#5c6bc0', '#26a69a', '#ec407a', '#7e57c2',
  '#42a5f5', '#d4e157', '#ff7043', '#78909c', '#ab47bc',
  '#29b6f6', '#8d6e63', '#66bb6a', '#ffa726', '#26c6da',
];

const getCustomCategoryColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  return CUSTOM_CATEGORY_COLORS[Math.abs(hash) % CUSTOM_CATEGORY_COLORS.length];
};

const getCategoryMeta = (categoryName) => {
  return ALL_CATEGORIES.find(cat => cat.name === categoryName) ||
    { name: categoryName, color: getCustomCategoryColor(categoryName), icon: '📌' };
};

// Helper function to extract entity names (same as in App.js)
const extractName = (details) => {
  // Get the part after the last dash, trim, and lowercase
  const parts = details.split('-');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase().trim() : details.toLowerCase().trim();
};

const buildDashboardStateFromOverview = (analysisId, userId, overview, createdAt) => ({
  id: analysisId,
  user_id: userId,
  created_at: createdAt,
  rows: [],
  columns: overview.columns || [],
  paid_type: overview.paid_type,
  daily_aggregated: overview.daily_aggregated,
  summary_totals: overview.summary_totals,
  all_entity_totals: overview.all_entity_totals || [],
  top_entities: overview.daily_aggregated?.top_entities,
  top_entity_names: overview.daily_aggregated?.top_entity_names,
  time_period: overview.time_period,
  rowsLoading: true,
});

function Dashboard() {
  const { user, getAuthHeaders, logout, verifyEmailConfirmation } = useAuth();
  
  // Email confirmation state
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(true);
  const [emailCheckError, setEmailCheckError] = useState('');

  // Upload states
  const [files, setFiles] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [, setUploadSuccess] = useState(''); // eslint-disable-line no-unused-vars
  const [password, setPassword] = useState(''); // New state for PDF password

  // Filter states
  const [period, setPeriod] = useState('all');
  const [date, setDate] = useState('');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // View mode state
  const [viewMode, setViewMode] = useState('withdrawn'); // 'withdrawn' or 'paid_in'
  
  // Category management states
  const [entityCategories, setEntityCategories] = useState({}); // Map of entity names to categories
  const [editingEntity, setEditingEntity] = useState(null); // Currently editing entity
  const [editingCategory, setEditingCategory] = useState(''); // Category being edited
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [dashboardView, setDashboardView] = useState('personal');
  const [expandedCategory, setExpandedCategory] = useState(null); // 'personal' | 'business'

  const activeCategories = dashboardView === 'business'
    ? (viewMode === 'paid_in' ? BUSINESS_INCOME_CATEGORIES : BUSINESS_EXPENSE_CATEGORIES)
    : (viewMode === 'paid_in' ? PERSONAL_INCOME_CATEGORIES : PERSONAL_EXPENSE_CATEGORIES);

  const [chartGranularity, setChartGranularity] = useState('daily');

  // Analysis states (same as analyzer page)
  const [loading, setLoading] = useState(false);
  const [error] = useState(''); // eslint-disable-line no-unused-vars
  const [result, setResult] = useState(null);
  const [originalResult, setOriginalResult] = useState(null); // Store original unfiltered data
  const [search, setSearch] = useState('');
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [, setHasAttemptedLoad] = useState(false); // Track if we've attempted to load data
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState('');

  const fetchAnalysisDashboardData = useCallback(async (analysisId, createdAt) => {
    const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
    const headers = await getAuthHeaders();
    const overviewRes = await axios.get(
      `${API_URL}/dashboard/analysis/${analysisId}/daily-aggregated`,
      { headers, params: { include_rows: false } }
    );
    const overview = overviewRes.data;
    if (overview.error) {
      throw new Error(overview.error);
    }

    const baseState = buildDashboardStateFromOverview(analysisId, user?.id, overview, createdAt);

    axios
      .get(`${API_URL}/dashboard/analysis/${analysisId}/transactions`, { headers })
      .then((txRes) => {
        if (txRes.data?.error || !txRes.data?.rows) return;
        const withRows = {
          ...baseState,
          rows: txRes.data.rows,
          columns: txRes.data.columns || baseState.columns,
          rowsLoading: false,
        };
        setResult((prev) => (prev?.id === analysisId ? withRows : prev));
        setOriginalResult((prev) => (prev?.id === analysisId ? withRows : prev));
      })
      .catch((err) => console.error('Background transaction load failed:', err));

    return baseState;
  }, [getAuthHeaders, user?.id]);
  const isMobile = useMediaQuery('(max-width:600px)');
  const dataLoadedRef = React.useRef(false);

  // Check email confirmation status on component mount
  useEffect(() => {
    const checkEmailConfirmation = async () => {
      try {
        setCheckingEmail(true);
        setEmailCheckError('');
        
        // Use the verification function from AuthContext
        const { confirmed, error } = await verifyEmailConfirmation();
        
        if (confirmed) {
          setEmailConfirmed(true);
        } else {
          setEmailConfirmed(false);
          setEmailCheckError(error || 'Please confirm your email address before accessing the dashboard');
          // Sign out user if email not confirmed
          await logout();
        }
      } catch (error) {
        console.error('Error checking email confirmation:', error);
        setEmailCheckError('Failed to verify email confirmation status');
      } finally {
        setCheckingEmail(false);
      }
    };

    // Only check email confirmation once when component mounts
    // This prevents the annoying constant refresh that was happening every 30 seconds
    checkEmailConfirmation();

    // Note: Email confirmation status is checked once on mount and doesn't change frequently
    // If a user needs to confirm their email, they can refresh the page manually
    // or the AuthContext will handle re-authentication as needed
  }, [verifyEmailConfirmation, logout]);

  // Note: Filters are now applied through the filteredData useMemo hook
  // which automatically updates when any filter changes

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    console.log('=== loadDashboardData function called ===');
    console.log('loadDashboardData called, user:', user);
    console.log('User ID:', user?.id);
    console.log('User email:', user?.email);
    
    // Only proceed if we have a user
    if (!user) {
      console.log('No user, skipping dashboard data load');
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (loading) {
      console.log('Already loading, skipping duplicate call');
      return;
    }
    
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      const headers = await getAuthHeaders();
      console.log('Dashboard load - Auth headers:', headers);
      console.log('API URL:', API_URL);
      
      // Try to load dashboard data, but don't fail if no auth
      try {
        console.log('Attempting to fetch analyses...');
        const analysesResponse = await axios.get(`${API_URL}/dashboard/analyses`, { headers });
        console.log('Analyses response status:', analysesResponse.status);
        console.log('Analyses response data:', analysesResponse.data);
        
        console.log('Attempting to fetch summary...');
        const summaryResponse = await axios.get(`${API_URL}/dashboard/summary`, { headers });
        console.log('Summary response status:', summaryResponse.status);
        console.log('Summary response data:', summaryResponse.data);
        
        const loadError =
          analysesResponse.data.error ||
          summaryResponse.data.error;
        if (loadError) {
          setSnackbar({
            open: true,
            message: 'Dashboard is taking longer than usual to load. Please refresh in a moment.',
            severity: 'warning',
          });
        }

        // Store the list of saved analyses for the dropdown
        const analysesList = analysesResponse.data.analyses || [];
        setSavedAnalyses(analysesList);

        // If there are saved analyses, load the most recent one
        if (analysesList.length > 0) {
          const mostRecentAnalysis = analysesList[0];
          setSelectedAnalysisId(mostRecentAnalysis.id);
          console.log('Loading most recent analysis:', mostRecentAnalysis.id);
          
          try {
            const enhancedData = await fetchAnalysisDashboardData(
              mostRecentAnalysis.id,
              mostRecentAnalysis.created_at
            );
            setResult(enhancedData);
            setOriginalResult(enhancedData);
          } catch (aggregationError) {
            console.error('Dashboard overview load failed:', aggregationError);
            setResult({
              id: mostRecentAnalysis.id,
              user_id: user.id,
              created_at: mostRecentAnalysis.created_at,
              error: 'Failed to load aggregated data',
            });
            setOriginalResult(null);
          }
        } else {
          console.log('No saved analyses found in response');
          console.log('Analyses array:', analysesResponse.data.analyses);
        }
      } catch (authError) {
        // If authentication fails, that's okay - just show empty state
        console.error('Error fetching dashboard data:', authError);
        console.error('Error response:', authError.response?.data);
        console.error('Error status:', authError.response?.status);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      // Don't set error for missing data - this is expected
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, loading, fetchAnalysisDashboardData]);

  const loadAnalysisById = useCallback(async (analysisId) => {
    if (!user || !analysisId) return;
    setLoading(true);
    try {
      const saved = savedAnalyses.find((a) => a.id === analysisId);
      const enhancedData = await fetchAnalysisDashboardData(analysisId, saved?.created_at);
      setResult(enhancedData);
      setOriginalResult(enhancedData);
      setSelectedAnalysisId(analysisId);
    } catch (err) {
      console.error('Error loading analysis:', err);
      setSnackbar({ open: true, message: 'Failed to load analysis', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [user, fetchAnalysisDashboardData, savedAnalyses]);

  // Safety timeout to prevent loading state from getting stuck
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        console.log('Loading timeout reached, resetting loading state');
        setLoading(false);
      }, 30000); // 30 second timeout
      
      return () => clearTimeout(timeout);
    }
  }, [loading]);






  // Load dashboard data only once on component mount when user is authenticated
  useEffect(() => {
    if (user && !dataLoadedRef.current && !loading) {
      console.log('User authenticated and data not loaded yet, loading dashboard data');
      dataLoadedRef.current = true; // Mark as loaded to prevent future calls
      setHasAttemptedLoad(true);
      loadDashboardData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]); // Removed loadDashboardData from dependencies to prevent infinite loop

      // Clear data when user logs out
  useEffect(() => {
    if (!user && (result || originalResult)) {
      console.log('User logged out, clearing data');
      setResult(null);
      setOriginalResult(null);
      setSavedAnalyses([]);
      setSelectedAnalysisId('');
      dataLoadedRef.current = false;
    }
  }, [user, result, originalResult]);

  // Monitor result state changes for debugging
  useEffect(() => {
    console.log('🔄 Result state changed:', {

              has_daily: !!result?.daily_aggregated,
      result_keys: result ? Object.keys(result) : []
    });
  }, [result]);

  // Cleanup effect to reset ref when component unmounts
  useEffect(() => {
    // This cleanup only runs when component unmounts
    return () => {
      console.log('Component unmounting, resetting dataLoadedRef');
      dataLoadedRef.current = false;
    };
  }, []); // Empty dependency array means this only runs on mount/unmount

  // Load saved categories from Supabase on component mount
  useEffect(() => {
    const loadCategoriesFromSupabase = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('entity_categories')
          .select('entity_name, category_name')
          .eq('user_id', user.id);
        
        if (error) {
          console.error('Error loading categories from Supabase:', error);
          return;
        }
        
        // Convert array of objects to entity-category mapping
        const categoriesMap = {};
        data.forEach(item => {
          categoriesMap[item.entity_name] = item.category_name;
        });
        
        setEntityCategories(categoriesMap);
      } catch (error) {
        console.error('Error loading categories from Supabase:', error);
      }
    };

    loadCategoriesFromSupabase();
  }, [user]);

  const allEntityNames = useMemo(() => {
    if (!result) return [];
    if (result.all_entity_totals?.length) {
      return result.all_entity_totals
        .map((entity) => (entity.details || '').trim())
        .filter(Boolean)
        .sort();
    }
    if (!result.rows || !result.columns) return [];
    const detailsIdx = result.columns.findIndex(col => col === 'details');
    if (detailsIdx === -1) return [];
    const allEntities = new Set();
    result.rows.forEach(row => {
      const details = row[detailsIdx];
      if (details && details.trim()) {
        allEntities.add(details.trim());
      }
    });
    return Array.from(allEntities).sort();
  }, [result]);

  // Auto-tag new entities that match previously tagged ones by extracted name
  useEffect(() => {
    if (!user || allEntityNames.length === 0 || Object.keys(entityCategories).length === 0) return;

    // Build reverse lookup: extracted name → category (from existing tags)
    const extractedToCategory = {};
    Object.entries(entityCategories).forEach(([fullName, category]) => {
      const extracted = extractName(fullName);
      if (extracted && !extractedToCategory[extracted]) {
        extractedToCategory[extracted] = category;
      }
    });

    // Find new entities that aren't tagged but whose extracted name matches a tagged one
    const newTags = {};
    allEntityNames.forEach(entityName => {
      if (entityCategories[entityName]) return; // already tagged
      const extracted = extractName(entityName);
      if (extracted && extractedToCategory[extracted]) {
        newTags[entityName] = extractedToCategory[extracted];
      }
    });

    if (Object.keys(newTags).length === 0) return;

    // Persist to Supabase and update local state
    const persistNewTags = async () => {
      const bulkData = Object.entries(newTags).map(([entity, category]) => ({
        user_id: user.id,
        entity_name: entity,
        category_name: category
      }));

      try {
        const { error } = await supabase
          .from('entity_categories')
          .upsert(bulkData, { onConflict: 'user_id,entity_name' });
        if (error) {
          console.error('Error auto-tagging entities:', error);
          return;
        }
      } catch (e) {
        console.error('Auto-tag exception:', e);
        return;
      }

      setEntityCategories(prev => ({ ...prev, ...newTags }));
      console.log(`Auto-tagged ${Object.keys(newTags).length} entities from previous tags`);
    };

    persistNewTags();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEntityNames, user]);

  // Keyword-based auto-tagging for entities that remain untagged after user/entity-name matching
  useEffect(() => {
    if (!user || allEntityNames.length === 0) return;

    const keywordTags = {};
    allEntityNames.forEach(entityName => {
      if (entityCategories[entityName]) return;
      const category = getKeywordCategory(entityName);
      if (category) keywordTags[entityName] = category;
    });

    if (Object.keys(keywordTags).length === 0) return;

    const persistKeywordTags = async () => {
      const bulkData = Object.entries(keywordTags).map(([entity, category]) => ({
        user_id: user.id,
        entity_name: entity,
        category_name: category,
      }));

      try {
        const { error } = await supabase
          .from('entity_categories')
          .upsert(bulkData, { onConflict: 'user_id,entity_name' });
        if (error) {
          console.error('Error keyword auto-tagging:', error);
          return;
        }
      } catch (e) {
        console.error('Keyword auto-tag exception:', e);
        return;
      }

      setEntityCategories(prev => ({ ...prev, ...keywordTags }));
      console.log(`Keyword auto-tagged ${Object.keys(keywordTags).length} entities`);
    };

    persistKeywordTags();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEntityNames, entityCategories, user]);

  // Save categories to Supabase whenever they change
  useEffect(() => {
    const saveCategoriesToSupabase = async () => {
      if (!user || Object.keys(entityCategories).length === 0) return;
      
      try {
        // Get current categories from Supabase
        const { data: existingData, error: fetchError } = await supabase
          .from('entity_categories')
          .select('entity_name, category_name')
          .eq('user_id', user.id);
        
        if (fetchError) {
          console.error('Error fetching existing categories:', fetchError);
          return;
        }
        
        // Create a map of existing categories
        const existingCategories = {};
        existingData.forEach(item => {
          existingCategories[item.entity_name] = item.category_name;
        });
        
        // Find categories to insert, update, or delete
        const toInsert = [];
        const toUpdate = [];
        const toDelete = [];
        
        // Check for new/updated categories
        Object.entries(entityCategories).forEach(([entity, category]) => {
          if (!existingCategories[entity]) {
            // New category
            toInsert.push({ user_id: user.id, entity_name: entity, category_name: category });
          } else if (existingCategories[entity] !== category) {
            // Updated category
            toUpdate.push({ user_id: user.id, entity_name: entity, category_name: category });
          }
        });
        
        // Check for deleted categories
        Object.keys(existingCategories).forEach(entity => {
          if (!entityCategories[entity]) {
            toDelete.push(entity);
          }
        });
        
        // Perform database operations
        if (toInsert.length > 0) {
          const { error: insertError } = await supabase
            .from('entity_categories')
            .insert(toInsert);
          if (insertError) console.error('Error inserting categories:', insertError);
        }
        
        if (toUpdate.length > 0) {
          for (const item of toUpdate) {
            const { error: updateError } = await supabase
              .from('entity_categories')
              .update({ category_name: item.category_name })
              .eq('user_id', user.id)
              .eq('entity_name', item.entity_name);
            if (updateError) console.error('Error updating category:', updateError);
          }
        }
        
        if (toDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from('entity_categories')
            .delete()
            .eq('user_id', user.id)
            .in('entity_name', toDelete);
          if (deleteError) console.error('Error deleting categories:', deleteError);
        }
        
      } catch (error) {
        console.error('Error saving categories to Supabase:', error);
      }
    };

    // Debounce the save operation to avoid too many database calls
    const timeoutId = setTimeout(saveCategoriesToSupabase, 1000);
    return () => clearTimeout(timeoutId);
  }, [entityCategories, user]);

  // Category management functions
  const handleCategoryEdit = (entityName) => {
    setEditingEntity(entityName);
    setEditingCategory(entityCategories[entityName] || '');
    setShowCategoryDialog(true);
  };

  const handleCategorySave = async () => {
    const entityKey = (editingEntity || '').trim();
    const categoryValue = (editingCategory || '').trim();
    if (!entityKey || !categoryValue || !user) {
      setShowCategoryDialog(false);
      setEditingEntity(null);
      setEditingCategory('');
      return;
    }

    try {
      const { error } = await supabase
        .from('entity_categories')
        .upsert({ user_id: user.id, entity_name: entityKey, category_name: categoryValue }, { onConflict: 'user_id,entity_name' });
      if (error) {
        console.error('Supabase upsert error:', error);
        setSnackbar({ open: true, message: 'Failed to save category', severity: 'error' });
      } else {
        setEntityCategories(prev => ({
          ...prev,
          [entityKey]: categoryValue
        }));
        setSnackbar({ open: true, message: 'Category saved', severity: 'success' });
      }
    } catch (e) {
      console.error('Category save exception:', e);
      setSnackbar({ open: true, message: 'Failed to save category', severity: 'error' });
    } finally {
      setShowCategoryDialog(false);
      setEditingEntity(null);
      setEditingCategory('');
    }
  };

  const handleCategoryCancel = () => {
    setShowCategoryDialog(false);
    setEditingEntity(null);
    setEditingCategory('');
  };

  const handleCategoryRemove = async (entityName) => {
    const entityKey = (entityName || '').trim();
    if (!user || !entityKey) return;
    try {
      const { error } = await supabase
        .from('entity_categories')
        .delete()
        .eq('user_id', user.id)
        .eq('entity_name', entityKey);
      if (error) {
        console.error('Supabase delete error:', error);
        setSnackbar({ open: true, message: 'Failed to remove category', severity: 'error' });
        return;
      }
      setEntityCategories(prev => {
        const newCategories = { ...prev };
        delete newCategories[entityKey];
        return newCategories;
      });
      setSnackbar({ open: true, message: 'Category removed', severity: 'success' });
    } catch (e) {
      console.error('Category remove exception:', e);
      setSnackbar({ open: true, message: 'Failed to remove category', severity: 'error' });
    }
  };

  const handleDeleteAllAnalyses = async () => {
    tracker.trackFeatureUse('delete_all_analyses', 'dashboard');
    setDeleteAllLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      const headers = await getAuthHeaders();
      const response = await axios.delete(`${API_URL}/dashboard/analyses/all`, { headers });
      const count = response.data.deleted_count || 0;
      setResult(null);
      setOriginalResult(null);
      setSavedAnalyses([]);
      setSelectedAnalysisId('');
      setSnackbar({ open: true, message: `Deleted ${count} saved ${count === 1 ? 'analysis' : 'analyses'}.`, severity: 'success' });
    } catch (err) {
      console.error('Error deleting all analyses:', err);
      setSnackbar({ open: true, message: 'Failed to delete analyses.', severity: 'error' });
    } finally {
      setDeleteAllLoading(false);
      setDeleteAllOpen(false);
    }
  };

  const handleCategoryImport = async (file) => {
    if (!user || !file) return;
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            setSnackbar({ open: true, message: 'No data found in the Excel file', severity: 'error' });
            return;
          }
          
          // Process imported data
          const importData = [];
          
          jsonData.forEach((row, index) => {
            const entity = row.Entity || row.entity || row['Entity Name'] || row['entity_name'];
            const category = row.Category || row.category || row['Category Name'] || row['category_name'];
            
            if (entity && category) {
              importData.push({
                user_id: user.id,
                entity_name: entity.trim(),
                category_name: category.trim()
              });
            }
          });
          
          if (importData.length === 0) {
            setSnackbar({ open: true, message: 'No valid entity-category pairs found', severity: 'error' });
            return;
          }
          
          // Insert imported categories
          const { error } = await supabase
            .from('entity_categories')
            .upsert(importData, { onConflict: 'user_id,entity_name' });
          
          if (error) {
            console.error('Error importing categories:', error);
            setSnackbar({ open: true, message: 'Failed to import categories', severity: 'error' });
            return;
          }
          
          // Update local state
          setEntityCategories(prev => {
            const newCategories = { ...prev };
            importData.forEach(item => {
              newCategories[item.entity_name] = item.category_name;
            });
            return newCategories;
          });
          
          setSnackbar({ open: true, message: `Successfully imported ${importData.length} categories`, severity: 'success' });
        } catch (error) {
          console.error('Error processing Excel file:', error);
          setSnackbar({ open: true, message: 'Failed to process Excel file', severity: 'error' });
        }
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setSnackbar({ open: true, message: 'Failed to read file', severity: 'error' });
    }
  };

  const getCategoryColor = (categoryName) => {
    return getCategoryMeta(categoryName).color;
  };

  const getCategoryIcon = (categoryName) => {
    return getCategoryMeta(categoryName).icon;
  };

  // Function definitions must come before useMemo hooks that use them


  // Function to apply all filters (view mode + period + amount)
  const applyAllFilters = (data, mode, periodFilter, dateFilter, customStartFilter, customEndFilter, minAmountFilter, maxAmountFilter, selectedEntitiesFilter, granularity = 'daily') => {
    console.log('applyAllFilters called with:', {
      hasData: !!data,
      hasRows: !!data?.rows,
      hasColumns: !!data?.columns,
      rowsLength: data?.rows?.length,
      mode,
      periodFilter,
      dateFilter,
      customStartFilter,
      customEndFilter,
      minAmountFilter,
      maxAmountFilter
    });
    
    // Debug: Log data structure for day filtering
    if (periodFilter === 'day' && data && data.rows) {
      console.log('Day filtering debug - data structure:', {
        totalRows: data.rows.length,
        columns: data.columns,
        completionTimeIdx: data.columns.indexOf('completion_time'),
        sampleRow: data.rows[0],
        dateFilter
      });
    }
    
    if (!data || !data.rows || !data.columns) {
      console.log('applyAllFilters: Missing required data structure');
      return data;
    }

    const withdrawnIdx = data.columns.findIndex(col => col === 'withdrawn');
    const paidInIdx = data.columns.findIndex(col => col === 'paid_in');
    const completionTimeIdx = data.columns.findIndex(col => col === 'completion_time');

    // Helper function to parse amounts
    const parseAmount = (value) => {
      if (!value || value === '') return 0;
      const cleaned = String(value).replace(/[,\s]/g, '').replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    };

    // Helper function to parse dates
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      try {
        // Handle various date formats from backend
        let date;
        if (typeof dateStr === 'string') {
          // Try parsing as ISO string first
          if (dateStr.includes('T') || dateStr.includes(' ')) {
            date = new Date(dateStr);
          } else if (dateStr.includes('-')) {
            // Handle YYYY-MM-DD format
            date = new Date(dateStr + 'T00:00:00');
          } else {
            // Fallback to direct parsing
            date = new Date(dateStr);
          }
        } else {
          date = new Date(dateStr);
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date:', dateStr);
          return null;
        }
        
        return date;
      } catch (error) {
        console.warn('Error parsing date:', dateStr, error);
        return null;
      }
    };

    // Filter rows based on all criteria
    const filteredRows = data.rows.filter(row => {
      // 1. View mode filter (withdrawn/paid_in)
      const withdrawnAmount = withdrawnIdx !== -1 ? parseAmount(row[withdrawnIdx]) : 0;
      const paidInAmount = paidInIdx !== -1 ? parseAmount(row[paidInIdx]) : 0;
      
      let passesViewMode = false;
      if (mode === 'withdrawn') {
        passesViewMode = withdrawnAmount > 0;
      } else if (mode === 'paid_in') {
        passesViewMode = paidInAmount > 0;
      } else {
        passesViewMode = true; // 'all' mode
      }

      if (!passesViewMode) return false;

      // 2. Period filter
        const transactionDate = parseDate(row[completionTimeIdx]);
        if (!transactionDate) return false;

      let passesPeriodFilter = false;
      if (periodFilter === 'today') {
        const today = new Date();
            const transactionDay = new Date(transactionDate.getFullYear(), transactionDate.getMonth(), transactionDate.getDate());
        const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        passesPeriodFilter = transactionDay.getTime() === todayDay.getTime();
      } else if (periodFilter === 'day' && dateFilter) {
        const filterDay = new Date(dateFilter);
        const transactionDay = new Date(transactionDate.getFullYear(), transactionDate.getMonth(), transactionDate.getDate());
        const filterDayNormalized = new Date(filterDay.getFullYear(), filterDay.getMonth(), filterDay.getDate());
        
        // Debug logging for day filtering
        if (process.env.NODE_ENV === 'development') {
          console.log('Day filter debug:', {
            dateFilter,
            filterDay: filterDay.toISOString(),
            filterDayNormalized: filterDayNormalized.toISOString(),
            transactionDate: transactionDate.toISOString(),
            transactionDay: transactionDay.toISOString(),
            match: transactionDay.getTime() === filterDayNormalized.getTime()
          });
        }
        
        passesPeriodFilter = transactionDay.getTime() === filterDayNormalized.getTime();
      } else if (periodFilter === 'week') {
        const today = new Date();
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() - today.getDay());
        weekStart.setHours(0, 0, 0, 0);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        passesPeriodFilter = transactionDate >= weekStart && transactionDate <= weekEnd;
      } else if (periodFilter === 'month') {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);
        passesPeriodFilter = transactionDate >= monthStart && transactionDate <= monthEnd;
      } else if (periodFilter === 'year') {
        const today = new Date();
        const yearStart = new Date(today.getFullYear(), 0, 1);
        const yearEnd = new Date(today.getFullYear(), 11, 31);
        yearEnd.setHours(23, 59, 59, 999);
        passesPeriodFilter = transactionDate >= yearStart && transactionDate <= yearEnd;
      } else if (periodFilter === 'custom' && customStartFilter && customEndFilter) {
        const startDate = new Date(customStartFilter);
        const endDate = new Date(customEndFilter);
        endDate.setHours(23, 59, 59, 999);
        passesPeriodFilter = transactionDate >= startDate && transactionDate <= endDate;
      } else {
        passesPeriodFilter = true; // 'all' period
      }

      if (!passesPeriodFilter) return false;

      // 3. Amount filter
      const amount = mode === 'withdrawn' ? withdrawnAmount : paidInAmount;
      if (minAmountFilter && amount < parseFloat(minAmountFilter)) return false;
      if (maxAmountFilter && amount > parseFloat(maxAmountFilter)) return false;

      // 4. Entity filter (if entities are selected)
      if (selectedEntitiesFilter && selectedEntitiesFilter.length > 0) {
        const detailsIdx = data.columns.findIndex(col => col === 'details');
        if (detailsIdx !== -1) {
          const details = row[detailsIdx];
          const detailsName = extractName(details);
          const matchesEntity = selectedEntitiesFilter.some(selectedEntity => {
            const selectedName = extractName(selectedEntity);
            // Check if the selected entity matches the transaction details using extracted names
            return detailsName.includes(selectedName) || selectedName.includes(detailsName);
          });
          if (!matchesEntity) return false;
        }
      }

      return true;
    });

    console.log('applyAllFilters result:', {
      originalRows: data.rows.length,
      filteredRows: filteredRows.length,
      filterApplied: data.rows.length !== filteredRows.length
    });
    
    // Debug: Log filtering results for day filtering
    if (periodFilter === 'day') {
      console.log('Day filtering results:', {
        originalRows: data.rows.length,
        filteredRows: filteredRows.length,
        dateFilter,
        periodFilter
      });
    }
    
    return {
      ...data,
      rows: filteredRows
    };
  };

  // Helper function to parse amounts (needed by getAnalysisViewData)
        const parseAmount = (value) => {
          if (!value || value === '') return 0;
          const cleaned = String(value).replace(/[,\s]/g, '').replace(/[^\d.-]/g, '');
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? 0 : parsed;
        };

  // Helper function to calculate entity total from filtered data
  const getEntityTotal = (entityName) => {
    if (!filteredData || !filteredData.rows || !filteredData.columns) return 0;
    
    const withdrawnIdx = filteredData.columns.findIndex(col => col === 'withdrawn');
    const paidInIdx = filteredData.columns.findIndex(col => col === 'paid_in');
    const detailsIdx = filteredData.columns.findIndex(col => col === 'details');
    
    if (detailsIdx === -1) return 0;

    // Determine amount column based on view mode
    let amountCol = 'withdrawn';
    if (viewMode === 'paid_in' && paidInIdx !== -1) {
      amountCol = 'paid_in';
    } else if (viewMode === 'withdrawn' && withdrawnIdx !== -1) {
      amountCol = 'withdrawn';
    } else if (viewMode === 'all') {
      // For 'all' mode, use the column with more data
      const withdrawnSum = withdrawnIdx !== -1 ? 
        filteredData.rows.reduce((sum, row) => sum + parseAmount(row[withdrawnIdx]), 0) : 0;
      const paidInSum = paidInIdx !== -1 ? 
        filteredData.rows.reduce((sum, row) => sum + parseAmount(row[paidInIdx]), 0) : 0;
      amountCol = withdrawnSum > paidInSum ? 'withdrawn' : 'paid_in';
    }

    const amountIdx = amountCol === 'withdrawn' ? withdrawnIdx : paidInIdx;
    if (amountIdx === -1) return 0;

    // Calculate total for this entity
    return filteredData.rows
      .filter(row => (row[detailsIdx] || '').trim() === entityName)
      .reduce((sum, row) => sum + parseAmount(row[amountIdx]), 0);
  };
        
  // Function to get daily analysis view data from filtered data
  const getAnalysisViewData = (filteredData, searchTerm, selectedEntities) => {
    if (!filteredData || !filteredData.rows || !filteredData.columns) {
      return {
        filteredEntityNames: [],
        allChartEntityNames: [],
        enhancedChartData: [],
        filteredTotal: 0,
        filteredTopEntities: [],
        filteredPreviewRows: [],
        dayAvg: 0,
        weekAvg: 0,
        monthAvg: 0
      };
    }

    console.log(`getAnalysisViewData called with filtered data:`, filteredData.rows.length, 'rows');
    console.log(`Using filtered daily view`);

    // Check if we have any active filters (including entity selection)
    const hasActiveFilters = period !== 'all' || date || customStart || customEnd || minAmount || maxAmount || viewMode !== 'withdrawn' || selectedEntities.length > 0;
    
    if (!hasActiveFilters && result?.daily_aggregated?.data) {
      // No active filters - use pre-aggregated data for better performance
      console.log(`Using pre-aggregated data (no filters active)`);
      const aggregatedData = result.daily_aggregated;
      
      // Calculate averages from pre-aggregated data
      const totalAmount = aggregatedData.data?.reduce((sum, day) => sum + (day.total_amount || 0), 0) || 0;
      const dayAvg = aggregatedData.data?.length > 0 ? totalAmount / aggregatedData.data.length : 0;
      
      // Calculate weekly average (group by weeks)
      const weeklyData = {};
      aggregatedData.data?.forEach(day => {
        const date = new Date(day.date);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = { total: 0, days: 0 };
        }
        weeklyData[weekKey].total += day.total_amount || 0;
        weeklyData[weekKey].days += 1;
      });
      
      const weekAvg = Object.values(weeklyData).length > 0 
        ? Object.values(weeklyData).reduce((sum, week) => sum + week.total, 0) / Object.values(weeklyData).length 
        : 0;
      
      // Calculate monthly average (group by months)
      const monthlyData = {};
      aggregatedData.data?.forEach(day => {
        const date = new Date(day.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { total: 0, days: 0 };
        }
        monthlyData[monthKey].total += day.total_amount || 0;
        monthlyData[monthKey].days += 1;
      });
      
      const monthAvg = Object.values(monthlyData).length > 0 
        ? Object.values(monthlyData).reduce((sum, month) => sum + month.total, 0) / Object.values(monthlyData).length 
        : 0;

      let allEntities;
      if (result?.all_entity_totals?.length && !filteredData.rows?.length) {
        allEntities = result.all_entity_totals
          .map((entity) => ({
            details: entity.details,
            total: entity.total || 0,
          }))
          .sort((a, b) => b.total - a.total);
      } else {
        const allEntityAmounts = {};
        filteredData.rows.forEach(row => {
          const detailsIdx = filteredData.columns.findIndex(col => col === 'details');
          const withdrawnIdx = filteredData.columns.findIndex(col => col === 'withdrawn');
          const paidInIdx = filteredData.columns.findIndex(col => col === 'paid_in');

          if (detailsIdx !== -1) {
            const entityName = (row[detailsIdx] || '').trim();
            if (entityName) {
              const amountIdx = withdrawnIdx !== -1 ? withdrawnIdx : paidInIdx;
              if (amountIdx !== -1) {
                const amount = parseAmount(row[amountIdx]);
                allEntityAmounts[entityName] = (allEntityAmounts[entityName] || 0) + amount;
              }
            }
          }
        });
        allEntities = Object.entries(allEntityAmounts).map(([entityName, total]) => ({
          details: entityName,
          total: total
        })).sort((a, b) => b.total - a.total);
      }
      const allEntityAmounts = Object.fromEntries(allEntities.map((e) => [e.details, e.total]));

      // Filter by search term and selected entities
      const filteredAllEntities = allEntities.filter(entity => {
        const matchesEntity = selectedEntities.length === 0 || selectedEntities.includes(entity.details);
        const matchesSearch = !searchTerm || searchTerm.trim() === '' || entity.details.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesEntity && matchesSearch;
      });

      const chartEntityNames = aggregatedData.top_entity_names || Object.keys(allEntityAmounts).slice(0, 10).concat(['Others']);

      return {
        filteredEntityNames: Object.keys(allEntityAmounts).filter(name => {
          const matchesSearch = !searchTerm || searchTerm.trim() === '' || name.toLowerCase().includes(searchTerm.toLowerCase());
          const normalizedSelected = selectedEntities.map(e => extractName(e));
          const nameExtracted = extractName(name);
          const matchesEntity = selectedEntities.length === 0 || 
            normalizedSelected.some(sel => nameExtracted.includes(sel) || sel.includes(nameExtracted));
          return matchesSearch && matchesEntity;
        }),
        allChartEntityNames: chartEntityNames,
        enhancedChartData: aggregatedData.data || [],
        filteredTotal: totalAmount,
        filteredTopEntities: filteredAllEntities,
        filteredPreviewRows: filteredData.rows,
        dayAvg,
        weekAvg,
        monthAvg
      };
    }

    // Active filters - generate new aggregated data from filtered raw data
    console.log(`Generating new aggregated data from filtered raw data`);

    // Get column indices
    const withdrawnIdx = filteredData.columns.findIndex(col => col === 'withdrawn');
    const paidInIdx = filteredData.columns.findIndex(col => col === 'paid_in');
    const completionTimeIdx = filteredData.columns.findIndex(col => col === 'completion_time');
    const detailsIdx = filteredData.columns.findIndex(col => col === 'details');

    if (completionTimeIdx === -1 || detailsIdx === -1) {
      console.error('Required columns not found in filtered data');
      return {
        filteredEntityNames: [],
        allChartEntityNames: [],
        enhancedChartData: [],
        filteredTotal: 0,
        filteredTopEntities: [],
        filteredPreviewRows: [],
        dayAvg: 0,
        weekAvg: 0,
        monthAvg: 0
      };
    }

    // Determine amount column based on view mode
    let amountCol = 'withdrawn';
    if (viewMode === 'paid_in' && paidInIdx !== -1) {
      amountCol = 'paid_in';
    } else if (viewMode === 'withdrawn' && withdrawnIdx !== -1) {
      amountCol = 'withdrawn';
    } else if (viewMode === 'all') {
      // For 'all' mode, use the column with more data
      const withdrawnSum = withdrawnIdx !== -1 ? 
        filteredData.rows.reduce((sum, row) => sum + parseAmount(row[withdrawnIdx]), 0) : 0;
      const paidInSum = paidInIdx !== -1 ? 
        filteredData.rows.reduce((sum, row) => sum + parseAmount(row[paidInIdx]), 0) : 0;
      amountCol = withdrawnSum > paidInSum ? 'withdrawn' : 'paid_in';
    }

    // Get top entities from filtered data
    const entityAmounts = {};
    filteredData.rows.forEach(row => {
      const details = row[detailsIdx];
      const amount = parseAmount(row[amountCol === 'withdrawn' ? withdrawnIdx : paidInIdx]);
      if (details && amount > 0) {
        entityAmounts[details] = (entityAmounts[details] || 0) + amount;
      }
    });

    // If entities are selected, prioritize them in the chart
    let topEntities, topEntityNames;
    if (selectedEntities && selectedEntities.length > 0) {
      // Include selected entities first, then top entities by amount
      const selectedEntityAmounts = {};
      selectedEntities.forEach(selectedEntity => {
        // Find matching entities in the data
        Object.entries(entityAmounts).forEach(([details, amount]) => {
          const detailsName = extractName(details);
          const selectedName = extractName(selectedEntity);
          if (detailsName.includes(selectedName) || selectedName.includes(detailsName)) {
            selectedEntityAmounts[details] = amount;
          }
        });
      });

      // Get remaining top entities by amount
      const remainingEntities = Object.entries(entityAmounts)
        .filter(([details]) => !Object.keys(selectedEntityAmounts).includes(details))
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10 - Object.keys(selectedEntityAmounts).length);

      // Combine selected and remaining entities
      topEntities = [
        ...Object.entries(selectedEntityAmounts).map(([details, total]) => ({ details, total })),
        ...remainingEntities.map(([details, total]) => ({ details, total }))
      ];
    } else {
      // No entities selected - use top entities by amount
      topEntities = Object.entries(entityAmounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([details, total]) => ({ details, total }));
    }

    topEntityNames = topEntities.map(e => e.details);
    topEntityNames.push('Others');

    // Group data by date and entity
    const groupedData = {};
    filteredData.rows.forEach(row => {
      const details = row[detailsIdx];
      const completionTime = row[completionTimeIdx];
      const amount = parseAmount(row[amountCol === 'withdrawn' ? withdrawnIdx : paidInIdx]);
      
      if (details && completionTime && amount > 0) {
        const date = completionTime.split(' ')[0]; // Get date part only
        
        if (!groupedData[date]) {
          groupedData[date] = {};
        }
        
        if (!groupedData[date][details]) {
          groupedData[date][details] = 0;
        }
        
        groupedData[date][details] += amount;
      }
    });

    // Convert grouped data to chart format
    const enhancedChartData = Object.entries(groupedData).map(([date, entities]) => {
      const chartDay = {
        date,
        total_amount: 0,
        transaction_count: 0,
        unique_entities: Object.keys(entities).length
      };

      // Add entity-specific amounts
      topEntityNames.forEach(entityName => {
        if (entityName === 'Others') {
          // Group all other entities
          const otherEntities = Object.keys(entities).filter(name => !topEntityNames.includes(name));
          chartDay[entityName] = otherEntities.reduce((sum, name) => sum + (entities[name] || 0), 0);
        } else {
          chartDay[entityName] = entities[entityName] || 0;
        }
        chartDay.total_amount += chartDay[entityName];
      });

      return chartDay;
    });

    // Sort by date
    enhancedChartData.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Get preview rows for table - filter by search and selected entities (same as App.js)
    const filteredPreviewRows = filteredData.rows.filter(row => {
      const detailsIdx = filteredData.columns.findIndex(col => col === 'details');
      if (detailsIdx === -1) return true;
      const detailsVal = (row[detailsIdx] || '').toString();
      const detailsName = extractName(detailsVal);
      const normalizedSelected = selectedEntities.map(e => extractName(e));
      const matchesEntity = selectedEntities.length === 0 ||
        normalizedSelected.some(sel => detailsName.includes(sel) || sel.includes(detailsName));
      const matchesSearch = !searchTerm || searchTerm.trim() === '' || detailsName.includes(searchTerm.toLowerCase().trim());
      return matchesEntity && matchesSearch;
    });

    // Calculate totals and averages
    const totalAmount = enhancedChartData.reduce((sum, day) => sum + day.total_amount, 0);
    const dayAvg = enhancedChartData.length > 0 ? totalAmount / enhancedChartData.length : 0;
    
    // Calculate weekly average (group by weeks)
    const weeklyData = {};
    enhancedChartData.forEach(day => {
      const date = new Date(day.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { total: 0, days: 0 };
      }
      weeklyData[weekKey].total += day.total_amount;
      weeklyData[weekKey].days += 1;
    });
    
    const weekAvg = Object.values(weeklyData).length > 0 
      ? Object.values(weeklyData).reduce((sum, week) => sum + week.total, 0) / Object.values(weeklyData).length 
      : 0;
    
    // Calculate monthly average (group by months)
    const monthlyData = {};
    enhancedChartData.forEach(day => {
      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { total: 0, days: 0 };
      }
      monthlyData[monthKey].total += day.total_amount;
      monthlyData[monthKey].days += 1;
    });
    
    const monthAvg = Object.values(monthlyData).length > 0 
      ? Object.values(monthlyData).reduce((sum, month) => sum + month.total, 0) / Object.values(monthlyData).length 
      : 0;

    console.log(`Generated chart data from filtered data:`, enhancedChartData.length, 'days');
    console.log(`Total amount:`, totalAmount);
    console.log(`Averages - Daily: ${dayAvg}, Weekly: ${weekAvg}, Monthly: ${monthAvg}`);
    console.log(`Top entities:`, topEntityNames.slice(0, 3));

    // Filter entity names by search term and selected entities (same as App.js)
    const filteredEntityNames = topEntityNames.filter(name => {
      const matchesSearch = !searchTerm || searchTerm.trim() === '' || name.toLowerCase().includes(searchTerm.toLowerCase());
      const normalizedSelected = selectedEntities.map(e => extractName(e));
      const nameExtracted = extractName(name);
      const matchesEntity = selectedEntities.length === 0 ||
        normalizedSelected.some(sel => nameExtracted.includes(sel) || sel.includes(nameExtracted));
      return matchesSearch && matchesEntity;
    });

    // Create all entities list (not just top entities)
    const allEntities = Object.entries(entityAmounts).map(([entityName, total]) => ({
      details: entityName,
      total: total
    })).sort((a, b) => b.total - a.total);

    // Filter all entities by search term and selected entities
    const filteredAllEntities = allEntities.filter(entity => {
      const matchesEntity = selectedEntities.length === 0 || selectedEntities.includes(entity.details);
      const matchesSearch = !searchTerm || searchTerm.trim() === '' || entity.details.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesEntity && matchesSearch;
    });

    return {
      filteredEntityNames,
      allChartEntityNames: topEntityNames, // Keep all for chart data
      enhancedChartData,
      filteredTotal: totalAmount,
      filteredTopEntities: filteredAllEntities, // Now includes ALL entities, not just top ones
      filteredPreviewRows,
      dayAvg,
      weekAvg,
      monthAvg
    };
  };

  // All useMemo hooks must be called before any conditional returns
  // Get filtered data for view mode - memoized to prevent infinite loops
  const filteredData = useMemo(() => {
    return applyAllFilters(result, viewMode, period, date, customStart, customEnd, minAmount, maxAmount, selectedEntities, 'daily');
  }, [result, viewMode, period, date, customStart, customEnd, minAmount, maxAmount, selectedEntities]);

  // Summary cards: respect period/amount/entity filters but always show both paid in and withdrawn
  const summaryFilteredData = useMemo(() => {
    return applyAllFilters(result, 'all', period, date, customStart, customEnd, minAmount, maxAmount, selectedEntities, 'daily');
  }, [result, period, date, customStart, customEnd, minAmount, maxAmount, selectedEntities]);

  const summaryTotals = useMemo(() => {
    const data = summaryFilteredData;
    if (!data?.columns) return null;

    if (!data.rows?.length) {
      if (result?.rowsLoading && result?.summary_totals) {
        return {
          totalIn: result.summary_totals.total_paid_in || 0,
          totalOut: result.summary_totals.total_withdrawn || 0,
          isLoading: true,
        };
      }
      return { totalIn: 0, totalOut: 0, isLoading: false };
    }

    const wIdx = data.columns.findIndex((col) => col === 'withdrawn');
    const pIdx = data.columns.findIndex((col) => col === 'paid_in');
    const totalOut = wIdx !== -1
      ? data.rows.reduce((sum, row) => sum + parseAmount(row[wIdx]), 0)
      : 0;
    const totalIn = pIdx !== -1
      ? data.rows.reduce((sum, row) => sum + parseAmount(row[pIdx]), 0)
      : 0;
    return { totalIn, totalOut, isLoading: false };
  }, [summaryFilteredData, result]);

  // Get analysis view data using the filtered data - memoized to prevent infinite loops
  const analysisData = useMemo(() => {
    return getAnalysisViewData(filteredData, search, selectedEntities);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, search, selectedEntities]);

  // Create stable chart data to prevent X-axis flickering
  const stableChartData = useMemo(() => {
    if (!analysisData.enhancedChartData) return [];
    
    // Create stable data objects that don't change reference unnecessarily
    return analysisData.enhancedChartData.map(day => ({
      ...day,
      // Ensure all properties are stable
      date: String(day.date), // Convert to stable string
      total_amount: Number(day.total_amount) || 0,
      transaction_count: Number(day.transaction_count) || 0,
      unique_entities: Number(day.unique_entities) || 0,
      // Add stable keys for each entity
      ...Object.fromEntries(
        analysisData.allChartEntityNames?.map(entityName => [
          entityName, 
          Number(day[entityName]) || 0
        ]) || []
      )
    }));
  }, [analysisData.enhancedChartData, analysisData.allChartEntityNames]);

  const granularChartData = useMemo(() => {
    if (!stableChartData || !stableChartData.length || chartGranularity === 'daily') {
      return stableChartData;
    }

    const entityNames = analysisData.allChartEntityNames || [];
    const buckets = {};

    stableChartData.forEach(day => {
      let key;
      if (chartGranularity === 'monthly') {
        key = day.date.slice(0, 7); // YYYY-MM
      } else {
        key = day.date.slice(0, 4); // YYYY
      }

      if (!buckets[key]) {
        buckets[key] = { date: key, total_amount: 0, transaction_count: 0, unique_entities: 0 };
        entityNames.forEach(name => { buckets[key][name] = 0; });
      }

      buckets[key].total_amount += day.total_amount || 0;
      buckets[key].transaction_count += day.transaction_count || 0;
      buckets[key].unique_entities += day.unique_entities || 0;
      entityNames.forEach(name => {
        buckets[key][name] += day[name] || 0;
      });
    });

    return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date));
  }, [stableChartData, chartGranularity, analysisData.allChartEntityNames]);

  // Data for line chart showing transaction counts over time
  const filteredLineChartData = useMemo(() => {
    if (!analysisData.rows || !analysisData.columns) return [];
    
    const detailsIdx = analysisData.columns.findIndex(col => col === 'details');
    const dateIdx = analysisData.columns.findIndex(col => col === 'completion_time');
    
    if (detailsIdx === -1 || dateIdx === -1) return [];
    
    // Filter rows by selectedEntities and search (same as preview table)
    const filteredRows = analysisData.rows.filter(row => {
      const detailsVal = (row[detailsIdx] || '').toString();
      const matchesEntity = selectedEntities.length === 0 || selectedEntities.includes(detailsVal);
      const matchesSearch = search.trim() === '' || detailsVal.toLowerCase().includes(search.toLowerCase());
      return matchesEntity && matchesSearch;
    });
    
    // Group by date (YYYY-MM-DD) and count transactions
    const dateMap = {};
    filteredRows.forEach(row => {
      const dateRaw = row[dateIdx];
      let dateStr = '';
      if (dateRaw) {
        const d = new Date(dateRaw);
        if (!isNaN(d)) {
          dateStr = d.toISOString().slice(0, 10);
        } else if (typeof dateRaw === 'string' && dateRaw.length >= 10) {
          dateStr = dateRaw.slice(0, 10);
        } else {
          dateStr = dateRaw;
        }
      }
      if (!dateMap[dateStr]) dateMap[dateStr] = 0;
      dateMap[dateStr] += 1; // Count transactions instead of summing amounts
    });
    
    // Convert to array and sort by date
    return Object.entries(dateMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [analysisData.rows, analysisData.columns, selectedEntities, search]);

  // Transform data for Chart.js
  const chartJsData = useMemo(() => {
    if (!granularChartData || !granularChartData.length || !analysisData.allChartEntityNames) {
      return { labels: [], datasets: [] };
    }

    const formatLabel = (d) => {
      if (chartGranularity === 'monthly') {
        const [y, m] = d.split('-');
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${monthNames[parseInt(m, 10) - 1]} ${y}`;
      }
      if (chartGranularity === 'yearly') return d;
      return d;
    };

    const labels = granularChartData.map(day => formatLabel(day.date));
    const datasets = analysisData.allChartEntityNames.map((entityName, index) => ({
      label: entityName,
      data: granularChartData.map(day => day[entityName] || 0),
      backgroundColor: COLORS[index % COLORS.length],
      borderColor: COLORS[index % COLORS.length],
      borderWidth: 1,
      stack: 'stack0'
    }));

    return { labels, datasets };
  }, [granularChartData, analysisData.allChartEntityNames, chartGranularity]);

  // Transform data for Chart.js Line Chart
  const chartJsLineData = useMemo(() => {
    if (!filteredLineChartData || !filteredLineChartData.length) {
      return { labels: [], datasets: [] };
    }

    const labels = filteredLineChartData.map(day => day.date);
    const datasets = [{
      label: `Transaction Count`,
      data: filteredLineChartData.map(day => day.count),
      borderColor: '#1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      borderWidth: 3,
      fill: true,
      tension: 0.4
    }];

    return { labels, datasets };
  }, [filteredLineChartData]);

  // Transform data for Category Pie Chart
  const categoryPieChartData = useMemo(() => {
    if (!filteredData || !filteredData.rows || !filteredData.columns) {
      return { labels: [], datasets: [] };
    }

    // Group entities by category - calculate from ALL filtered transaction data
    const categoryTotals = {};
    const categoryColors = {};
    
    // Get column indices
    const withdrawnIdx = filteredData.columns.findIndex(col => col === 'withdrawn');
    const paidInIdx = filteredData.columns.findIndex(col => col === 'paid_in');
    const detailsIdx = filteredData.columns.findIndex(col => col === 'details');
    
    if (detailsIdx === -1) {
      return { labels: [], datasets: [] };
    }

    // Determine amount column based on view mode
    let amountCol = 'withdrawn';
    if (viewMode === 'paid_in' && paidInIdx !== -1) {
      amountCol = 'paid_in';
    } else if (viewMode === 'withdrawn' && withdrawnIdx !== -1) {
      amountCol = 'withdrawn';
    } else if (viewMode === 'all') {
      // For 'all' mode, use the column with more data
      const withdrawnSum = withdrawnIdx !== -1 ? 
        filteredData.rows.reduce((sum, row) => sum + parseAmount(row[withdrawnIdx]), 0) : 0;
      const paidInSum = paidInIdx !== -1 ? 
        filteredData.rows.reduce((sum, row) => sum + parseAmount(row[paidInIdx]), 0) : 0;
      amountCol = withdrawnSum > paidInSum ? 'withdrawn' : 'paid_in';
    }

    const amountIdx = amountCol === 'withdrawn' ? withdrawnIdx : paidInIdx;
    if (amountIdx === -1) {
      return { labels: [], datasets: [] };
    }

    // Process all transactions to calculate category totals
    filteredData.rows.forEach(row => {
      const entityName = (row[detailsIdx] || '').trim();
      const amount = parseAmount(row[amountIdx]);
      
      if (amount > 0) {
        const category = entityCategories[entityName] || 'Uncategorized';
        categoryTotals[category] = (categoryTotals[category] || 0) + amount;
        
        // Get category color
        if (!categoryColors[category]) {
          categoryColors[category] = getCategoryMeta(category).color;
        }
      }
    });

    // Convert to chart format
    const labels = Object.keys(categoryTotals);
    const data = Object.values(categoryTotals);
    const backgroundColor = Object.values(categoryColors);

    return {
      labels,
      datasets: [{
        data,
        backgroundColor,
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  }, [filteredData, entityCategories, viewMode]);

  // Chart.js options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend to prevent chart squishing on mobile
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'KES',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 11
          }
        }
      },
      y: {
        stacked: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          callback: function(value) {
            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}K`;
            }
            return value.toLocaleString();
          },
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  }), []);

  // Chart.js options for Line Chart
  const chartJsLineOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend to prevent chart squishing on mobile 
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          callback: function(value) {
            return value.toLocaleString();
          },
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  }), []);

  // Chart.js options for Pie Chart
  const chartJsPieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: isMobile ? 10 : 15,
          usePointStyle: true,
          font: {
            size: isMobile ? 10 : 12
          },
          boxWidth: isMobile ? 12 : 15,
          maxWidth: isMobile ? 180 : 300,
          generateLabels: (chart) => {
            const data = chart.data;
            if (!data?.labels || !data?.datasets?.length) return [];
            const dataset = data.datasets[0] || { data: [], backgroundColor: [] };
            const values = Array.isArray(dataset.data) ? dataset.data : [];
            const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
            const toKES = (val) => new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'KES',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(val || 0);
            return data.labels.map((label, i) => {
              const value = Number(values[i]) || 0;
              const percentage = total ? ((value / total) * 100).toFixed(1) : '0.0';
              const bg = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[i] : dataset.backgroundColor;
              
              // Shorten text on mobile
              const shortKES = (val) => isMobile 
                ? `KSh ${Math.round(val).toLocaleString()}`
                : toKES(val);
              
              const displayText = isMobile 
                ? `${label}: ${shortKES(value)} (${percentage}%)`
                : `${label}: ${toKES(value)} (${percentage}%)`;
              
              return {
                text: displayText,
                fillStyle: bg || '#ccc',
                strokeStyle: bg || '#ccc',
                lineWidth: 1,
                hidden: !value,
                index: i,
              };
            });
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'KES',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value)} (${percentage}%)`;
          }
        }
      }
    }
  }), [isMobile]);



  // Mobile columns for DataGrid - memoized
  const mobileColumns = useMemo(() => {
    return ["receiptno", "completion_time", "details", "paid_in", "withdrawn"];
  }, []);
  
  // Update columns for DataGrid to show the appropriate column based on viewMode - memoized
  const baseColumns = useMemo(() => {
    return result && result.columns
      ? result.columns.filter(col => col !== 'transactionstatus')
      : [];
  }, [result]);
  
  const columns = useMemo(() => {
    return baseColumns.map(col => {
      // Define column-specific configurations for better visibility
      let columnConfig = {
        field: col,
        headerName: col,
        sortable: true,
        filterable: true,
        hide: isMobile && !mobileColumns.includes(col),
      };

      // Set specific widths and flex values for different columns
      switch (col) {
        case 'details':
          columnConfig.flex = 3; // Make details column wider
          columnConfig.minWidth = isMobile ? 200 : 300;
          break;
        case 'completion_time':
          columnConfig.flex = 1.5;
          columnConfig.minWidth = isMobile ? 120 : 160;
          break;
        case 'paid_in':
        case 'withdrawn':
          columnConfig.flex = 1.2;
          columnConfig.minWidth = isMobile ? 100 : 120;
          break;
        case 'receiptno':
          columnConfig.flex = 1;
          columnConfig.minWidth = isMobile ? 80 : 100;
          break;
        default:
          columnConfig.flex = 1;
          columnConfig.minWidth = isMobile ? 80 : 100;
          break;
      }

      return columnConfig;
    });
  }, [baseColumns, mobileColumns, isMobile]);

  // Show loading while checking email confirmation
  if (checkingEmail) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Verifying email confirmation...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Show error if email not confirmed
  if (!emailConfirmed) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Email color="warning" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom color="warning.main">
            Email Confirmation Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {emailCheckError || 'Please check your email and click the confirmation link before accessing the dashboard.'}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
            sx={{ mr: 2 }}
          >
            Refresh Page
          </Button>
          <Button 
            variant="outlined" 
            onClick={logout}
          >
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  



  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setPassword(''); // Clear password when files change
  };

  const handleUpload = async () => {
    if (!files || files.length === 0) {
      setUploadError('Please upload at least one PDF file.');
      return;
    }
    tracker.trackFeatureUse('dashboard_upload', 'dashboard', { file_count: files.length });
    setUploadError('');
    setUploadLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('paid_type', 'all'); // Request all data, not just withdrawn
      formData.append('period', period);
      formData.append('date', date);
      formData.append('custom_start', customStart ? format(customStart, 'yyyy-MM-dd') : '');
      formData.append('custom_end', customEnd ? format(customEnd, 'yyyy-MM-dd') : '');
        formData.append('min_amount', minAmount);
        formData.append('max_amount', maxAmount);
      formData.append('password', password); // Add PDF password
      formData.append('save_to_dashboard', 'true');
      formData.append('combine_with_existing', 'true'); // Always combine with existing in dashboard
      
      console.log('Upload - save_to_dashboard set to true');
      console.log('Upload - Form data contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }
      
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      const authHeaders = await getAuthHeaders();
      console.log('Dashboard upload - Raw auth headers:', authHeaders);
      
      // Create headers object without overriding Content-Type
      const headers = {
        ...authHeaders,
        // Don't set Content-Type - let axios set it automatically for FormData
      };
      
      console.log('Dashboard upload - Final headers:', headers);
      console.log('Upload - API URL:', API_URL);
      
      const response = await axios.post(`${API_URL}/upload-pdf/`, formData, {
        headers: headers,
      });
      
      console.log('Upload response status:', response.status);
      console.log('Upload response data:', response.data);
      
      if (response.data.error) {
        const errMsg = getUploadErrorMessage(response.data);
        setUploadError(errMsg);
        setSnackbar({ open: true, message: errMsg, severity: 'error' });
      } else {
        const partialResult = response.data.partial_result;
        let successMessage = '';
        let severity = 'success';
        
        // Check if this is a partial result (quota limited)
        if (partialResult && partialResult.is_partial) {
          successMessage = partialResult.upgrade_message || 
            `Showing partial results: ${partialResult.transactions_shown.toLocaleString()} of ~${partialResult.estimated_total_transactions.toLocaleString()} transactions`;
          severity = 'warning';
        } else {
          successMessage = 'Files uploaded successfully! Analysis updated.';
          if (response.data.processing_info && response.data.processing_info.combined_with_existing) {
            successMessage += ` Combined with ${response.data.processing_info.existing_transactions_added} existing transactions.`;
          }
        }
        
        setUploadSuccess(successMessage);
        setSnackbar({ open: true, message: successMessage, severity });

        // When saved, load the analysis via daily-aggregated so charts and dropdown get correct shape
        if (response.data.analysis_id) {
          console.log('Analysis saved with ID:', response.data.analysis_id);
          setSelectedAnalysisId(response.data.analysis_id);
          try {
            const API_URL2 = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
            const refreshHeaders = await getAuthHeaders();
            const analysesRes = await axios.get(`${API_URL2}/dashboard/analyses`, { headers: refreshHeaders });
            setSavedAnalyses(analysesRes.data.analyses || []);
            const enhancedData = await fetchAnalysisDashboardData(response.data.analysis_id);
            setResult(enhancedData);
            setOriginalResult(enhancedData);
          } catch (err) {
            console.error('Failed to refresh dashboard after save:', err);
            setResult(response.data);
            setOriginalResult(response.data);
          }
        } else {
          setResult(response.data);
          setOriginalResult(response.data);
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      console.error('Upload error response:', err.response?.data);
      console.error('Upload error status:', err.response?.status);
      // Surface quota / plan-related errors from backend
      const errorDetail = err?.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail?.message) {
        const baseMessage = getUploadErrorMessage(errorDetail);
        const upgradeMessage = errorDetail.upgrade_message ? ` ${errorDetail.upgrade_message}` : '';
        setUploadError(baseMessage);
        setSnackbar({ open: true, message: `${baseMessage}${upgradeMessage}`.trim(), severity: 'warning' });
      } else if (typeof errorDetail === 'string') {
        setUploadError(errorDetail);
        setSnackbar({ open: true, message: errorDetail, severity: 'error' });
      } else {
        setUploadError('Failed to upload files.');
        setSnackbar({ open: true, message: 'Failed to upload files.', severity: 'error' });
      }
    } finally {
      setUploadLoading(false);
    }
  };





  // Export to Excel handler (same as analyzer page)
  const handleExportExcel = () => {
    if (!filteredData || !filteredData.columns || !filteredData.rows || !filteredData.rows.length) return;
    tracker.trackFeatureUse('dashboard_export_excel', 'dashboard');
    
    const completionTimeIdx = filteredData.columns.findIndex(col => col === 'completion_time');
    let exportColumns = [...filteredData.columns];
    
    if (completionTimeIdx !== -1) {
      exportColumns = [
        ...filteredData.columns.slice(0, completionTimeIdx),
        'date',
        'time',
        ...filteredData.columns.slice(completionTimeIdx + 1)
      ];
    }

    const exportRows = filteredData.rows.map(row => {
      if (completionTimeIdx !== -1) {
        const newRow = [
          ...row.slice(0, completionTimeIdx),
          row[completionTimeIdx] ? row[completionTimeIdx].split(' ')[0] : '',
          row[completionTimeIdx] ? row[completionTimeIdx].split(' ')[1] : '',
          ...row.slice(completionTimeIdx + 1)
        ];
        return newRow;
      }
      return row;
    });

    const topEntities = analysisData?.filteredTopEntities || [];
    const totalSum = topEntities.reduce((sum, e) => sum + e.total, 0);
    const topEntitiesRows = topEntities.map(entity => {
      const percent = totalSum > 0 ? (entity.total / totalSum) * 100 : 0;
      return [entity.details, entity.total, percent];
    });

    const wsTransactions = typedTransactionsSheet(exportColumns, exportRows);
    const wsTopEntities = typedTopEntitiesSheet(topEntitiesRows);
    
    // Create workbook with both sheets
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsTopEntities, 'Top People & Businesses');
    XLSX.utils.book_append_sheet(wb, wsTransactions, 'Transactions');
    
    XLSX.writeFile(wb, `Mobile_Money_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleDownloadChart = async () => {
    tracker.trackFeatureUse('download_chart', 'dashboard');
    try {
      // Chart.js provides a built-in method to get the canvas
      const canvas = document.querySelector('canvas');
      if (!canvas) {
        console.error('Chart canvas not found');
        return;
      }
      
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `Mobile_Money_Analysis_${timestamp}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading chart:', error);
    }
  };



    
    // Check if we have pre-aggregated data for better performance


  return (
    <Box sx={{ minHeight: '100vh', background: '#f7fafc' }}>
    <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          <Typography variant="body2" component="span">
            Want to remove your account and all stored data?{' '}
            <Box
              component="a"
              href="/delete-account"
              sx={{ color: 'primary.dark', fontWeight: 600, textDecoration: 'underline', display: 'inline' }}
            >
              Delete your account
            </Box>
          </Typography>
        </Alert>

        <MobileAppCta compact />

        {/* Compact Upload and Filters Section */}
        <Grid container spacing={1} alignItems="center" sx={{ p: 1.5, mb: 1.5 }}>
                  {/* Upload Section */}
            <Grid item xs={12} sm={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 'fit-content' }}>Upload:</Typography>
                  <Button 
                  variant="outlined" 
                    component="label" 
                  size="small"
                  sx={{ minWidth: 'fit-content' }}
                  >
                  Files
                    <input type="file" accept="application/pdf" multiple hidden onChange={handleFileChange} />
                  </Button>
                  {files.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ({files.length})
                      </Typography>
                )}
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleUpload} 
                  disabled={uploadLoading || files.length === 0}
                  size="small"
                >
                  {uploadLoading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
                </Button>
                {files.length > 0 && (
                  <Typography variant="caption" color="primary" sx={{ ml: 0.5, fontStyle: 'italic' }}>
                    Click to upload
                  </Typography>
                )}

              </Stack>
            </Grid>

            {/* PDF Password Field */}
            <Grid item xs={12} sm={2}>
              <TextField
                label="PDF Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoComplete="off"
                variant="outlined"
                size="small"
                placeholder="if any"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* View Toggle */}
            <Grid item xs={12} sm={2.5}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 'fit-content' }}>View:</Typography>
                <Button
                  variant={viewMode === 'withdrawn' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('withdrawn')}
                  size="small"
                  color="primary"
                  sx={{ minWidth: 'fit-content', px: 1 }}
                >
                  Withdrawn
                </Button>
                <Button
                  variant={viewMode === 'paid_in' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('paid_in')}
                  size="small"
                  color="success"
                  sx={{ minWidth: 'fit-content', px: 1 }}
                >
                  Paid In
                </Button>
              </Stack>
            </Grid>

            {/* Performance-Optimized Chart View Switching */}
            <Grid item xs={12} sm={2.5}>
              <Stack direction="row" spacing={1} alignItems="center">




              </Stack>
                </Grid>
                
            {/* Period Filter */}
            <Grid item xs={12} sm={1.5}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                        >
                          <MenuItem value="all">All Time</MenuItem>
                          <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="day">Specific Day</MenuItem>
                          <MenuItem value="week">This Week</MenuItem>
                          <MenuItem value="month">This Month</MenuItem>
                          <MenuItem value="year">This Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                        </Select>
                      </FormControl>
            </Grid>

            {/* Date Filter (for specific day) */}
                    {period === 'day' && (
              <Grid item xs={12} sm={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ fontSize: '0.875rem' }}
                />
              </Grid>
            )}

            {/* Custom Date Range */}
                    {period === 'custom' && (
              <>
                <Grid item xs={12} sm={1.5}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={customStart}
                          onChange={setCustomStart}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Start" />}
                        />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={customEnd}
                          onChange={setCustomEnd}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="End" />}
                        />
                  </LocalizationProvider>
                </Grid>
              </>
                    )}
                    
            {/* Amount Filters */}
            <Grid item xs={12} sm={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                placeholder="Min"
                        type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                sx={{ fontSize: '0.875rem' }}
              />
            </Grid>
            <Grid item xs={12} sm={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                placeholder="Max"
                type="number"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                sx={{ fontSize: '0.875rem' }}
                    />
            </Grid>
                    

              </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 0.5 }}>
            Up to {ANALYZER_MAX_PAGES_PER_FILE} pages per PDF per upload. Longer statements?{' '}
            <Box component="a" href="/" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              Split on Analyzer
            </Box>
            {' · '}
            <Box component="a" href="/split-pdf" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              Split PDF page
            </Box>
          </Typography>
              
          {/* Error Messages and Actions */}
              {uploadError && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="error" size="small" sx={{ py: 0 }}>{uploadError}</Alert>
                  </Box>
          )}

          {/* Saved Analyses Selector & Actions */}
          {savedAnalyses.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1, mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                  Saved Analyses:
                </Typography>
                <FormControl size="small" sx={{ minWidth: isMobile ? 180 : 280 }}>
                  <Select
                    value={selectedAnalysisId}
                    onChange={(e) => loadAnalysisById(e.target.value)}
                    displayEmpty
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {savedAnalyses.map((a) => {
                      const dateStr = a.created_at
                        ? new Date(a.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : a.id.slice(0, 8);
                      const txCount = a.total_transactions ? ` (${a.total_transactions.toLocaleString()} txns)` : '';
                      return (
                        <MenuItem key={a.id} value={a.id}>
                          {dateStr}{txCount}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
              {result && (
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  startIcon={<DeleteForever fontSize="small" />}
                  onClick={() => setDeleteAllOpen(true)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Delete All
                </Button>
              )}
            </Stack>
          )}

        {/* Dashboard Content - Original Analyzer Visuals */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
                  </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : result ? (
          <>
            {/* Partial Results Banner - Show when quota limited the analysis */}
            {result.partial_result && result.partial_result.is_partial && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>
                      Partial results ({result.partial_result.pages_processed}/{result.partial_result.total_pages} pages in this run)
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                      {result.partial_result.upgrade_message ||
                        `Showing ${result.partial_result.transactions_shown.toLocaleString()} of ~${result.partial_result.estimated_total_transactions.toLocaleString()} estimated transactions. Upgrade to unlock more pages in one run when your plan allows.`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      ~{result.partial_result.estimated_total_transactions.toLocaleString()} transactions estimated in the full PDF · {result.partial_result.transactions_shown.toLocaleString()} in this result.
                    </Typography>
                  </Box>
                  {!result.partial_result.server_page_cap_applied && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => (window.location.href = '/#subscription')}
                    >
                      Upgrade / Buy Day Pass
                    </Button>
                  )}
                </Stack>
              </Alert>
            )}

            {/* Dashboard View Toggle */}
            <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <ToggleButtonGroup
                value={dashboardView}
                exclusive
                onChange={(e, val) => { if (val != null) { setDashboardView(val); tracker.trackClick(`dashboard_view_${val}`, 'dashboard'); } }}
                size="small"
              >
                <ToggleButton value="personal" aria-label="personal view">
                  <Person sx={{ mr: 0.5, fontSize: 18 }} /> Personal
                </ToggleButton>
                <ToggleButton value="business" aria-label="business view">
                  <Business sx={{ mr: 0.5, fontSize: 18 }} /> Business
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                {dashboardView === 'business'
                  ? 'Track business income, expenses, and profit/loss'
                  : 'Track personal spending by category'}
              </Typography>
            </Paper>

            {/* Income / Expenses / Balance Summary — filtered by period, amount, and entity (not view toggle) */}
            {summaryTotals && (() => {
              const { totalIn, totalOut, isLoading } = summaryTotals;
              const net = totalIn - totalOut;
              const isBusiness = dashboardView === 'business';
              return (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" gap={1}>
                    <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">{isBusiness ? 'Income' : 'Paid In'}</Typography>
                      <Typography variant="h6" color="success.main" fontWeight={700}>
                        KSh {totalIn.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">{isBusiness ? 'Expenses' : 'Withdrawn'}</Typography>
                      <Typography variant="h6" color="error.main" fontWeight={700}>
                        KSh {totalOut.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                      <Typography variant="caption" color="text.secondary">{isBusiness ? 'Net Profit / Loss' : 'Balance'}</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: net >= 0 ? 'success.main' : 'error.main' }}>
                        {net >= 0 ? '+' : ''}KSh {net.toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                  {isLoading && (
                    <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
                      Updating totals when transactions finish loading…
                    </Typography>
                  )}
                </Paper>
              );
            })()}

            {/* Filter Status */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Active Filters:
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                <Chip 
                  label={`View: ${viewMode}`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
                <Chip 
                  label={`Period: ${period}`} 
                  size="small" 
                  color={period === 'all' ? 'success' : 'secondary'} 
                  variant="outlined" 
                />
                {period === 'day' && date && (
                  <Chip 
                    label={`Date: ${date}`} 
                    size="small" 
                    color="info" 
                    variant="outlined" 
                  />
                )}
                {period === 'custom' && customStart && customEnd && (
                  <Chip 
                    label={`Range: ${customStart} to ${customEnd}`} 
                    size="small" 
                    color="info" 
                    variant="outlined" 
                  />
                )}
                {minAmount && (
                  <Chip 
                    label={`Min: ${minAmount}`} 
                    size="small" 
                    color="warning" 
                    variant="outlined" 
                  />
                )}
                {maxAmount && (
                  <Chip 
                    label={`Max: ${maxAmount}`} 
                    size="small" 
                    color="warning" 
                    variant="outlined" 
                  />
                )}
                <Chip 
                  label={
                    result?.rowsLoading
                      ? `Loading transactions… (${result?.summary_totals?.transaction_count?.toLocaleString() || '…'} total)`
                      : `Showing: ${filteredData?.rows?.length || 0} of ${result?.rows?.length || result?.summary_totals?.transaction_count || 0} transactions`
                  }
                  size="small" 
                  color="success" 
                  variant="outlined" 
                />
                {Object.keys(entityCategories).length > 0 && (
                  <Chip 
                    label={`${Object.keys(entityCategories).length} entities tagged`} 
                    size="small" 
                    color="info" 
                    variant="outlined" 
                    icon={<Category />}
                  />
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPeriod('all');
                    setDate('');
                    setCustomStart(null);
                    setCustomEnd(null);
                    setMinAmount('');
                    setMaxAmount('');
                    setViewMode('withdrawn');
                  }}
                  sx={{ ml: 1 }}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Category />}
                  onClick={() => setShowCategoryDialog(true)}
                  sx={{ ml: 1 }}
                >
                  Manage Categories
                </Button>
              </Stack>
            </Paper>

            {/* Category Distribution Pie Chart */}
            {categoryPieChartData.labels.length > 0 && (
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Transaction Distribution by Category
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Distribution of transactions by tagged categories. Tag entities above to see category breakdown.
                </Typography>
                
                {/* Statistics Summary */}
                <Typography variant="h5" align="center" gutterBottom sx={{ mb: 2 }}>
                  Total {viewMode === 'withdrawn' ? 'Withdrawn' : 'Paid In'}: {analysisData.filteredTotal.toLocaleString()}
                </Typography>
                
                <Stack 
                  direction="row" 
                  spacing={{ xs: 1, sm: 4 }} 
                  justifyContent="center" 
                  alignItems="center"
                  sx={{ mb: 2, flexWrap: 'wrap' }}
                >
                  <Typography variant="caption" color="success.main" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                    <Tooltip title="Average daily amount across all dates in the filtered data">
                      <span>
                        Daily (Avg.): <b>{analysisData.dayAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                      </span>
                    </Tooltip>
                  </Typography>
                  <Typography variant="caption" color="success.main" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                    <Tooltip title="Average weekly amount across all weeks in the filtered data">
                      <span>
                        Weekly (Avg.): <b>{analysisData.weekAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                      </span>
                    </Tooltip>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                    <Tooltip title="Average monthly amount across all months in the filtered data">
                      <span>
                        Monthly (Avg.): <b>{analysisData.monthAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                      </span>
                    </Tooltip>
                  </Typography>
                </Stack>
                
                <Box sx={{ height: { xs: 500, sm: 400 }, minHeight: { xs: 500, sm: 400 } }}>
                  <Pie data={categoryPieChartData} options={chartJsPieOptions} />
                </Box>
              </Paper>
            )}

            {/* Category Management Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Category Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Manage categories for your entities. Tag entities above to see them appear in the category breakdown.
              </Typography>

              {/* Category Statistics Summary */}
              <Box sx={{ mb: isMobile ? 2 : 3, p: isMobile ? 1.5 : 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1.5 : 3} alignItems="center" flexWrap="wrap">
                  <Box sx={{ textAlign: 'center', flex: 1, minWidth: isMobile ? 'auto' : '120px' }}>
                    <Typography variant="h6" color="primary">
                      {Object.keys(entityCategories).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Entities Tagged
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', flex: 1, minWidth: isMobile ? 'auto' : '120px' }}>
                    <Typography variant="h6" color="success.main">
                      {new Set(Object.values(entityCategories)).size}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Categories Used
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', flex: 1, minWidth: isMobile ? 'auto' : '120px' }}>
                    <Typography variant="h6" color="info.main">
                      {allEntityNames.length - Object.keys(entityCategories).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Untagged Entities
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', flex: 1, minWidth: isMobile ? 'auto' : '120px' }}>
                    <Typography variant="h6" color="warning.main">
                      {Math.round((Object.keys(entityCategories).length / Math.max(allEntityNames.length, 1)) * 100)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Coverage
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Category Import/Export */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Import/Export Categories
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Import categories from Excel or export your current categories.
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                  <Button
                    variant="outlined"
                    component="label"
                    size="small"
                    startIcon={<span>📥</span>}
                  >
                    Import Categories
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleCategoryImport(file);
                        }
                      }}
                    />
                  </Button>
                  {Object.keys(entityCategories).length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<span>📤</span>}
                      onClick={() => {
                        const categoryData = Object.entries(entityCategories).map(([entity, category]) => ({
                          Entity: entity,
                          Category: category
                        }));
                        const ws = XLSX.utils.json_to_sheet(categoryData);
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, 'Categories');
                        XLSX.writeFile(wb, `Statement_Categories_Template_${new Date().toISOString().slice(0, 10)}.xlsx`);
                      }}
                    >
                      Download Template
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Import: Excel with Entity and Category columns | Export: Download template format
                  </Typography>
                </Stack>
              </Box>

              <Stack spacing={0}>
                {(() => {
                  const usedCategoryNames = [...new Set(Object.values(entityCategories))];
                  const allCategoryMetas = usedCategoryNames.map(name => getCategoryMeta(name));

                  const categoryData = allCategoryMetas.map((category) => {
                    const categoryEntities = Object.entries(entityCategories)
                      .filter(([, cat]) => cat === category.name)
                      .map(([entity]) => ({ name: entity, total: getEntityTotal(entity) }))
                      .sort((a, b) => b.total - a.total);
                    const categoryTotal = categoryEntities.reduce((sum, e) => sum + e.total, 0);
                    return { ...category, entities: categoryEntities, entityCount: categoryEntities.length, total: categoryTotal };
                  }).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

                  const grandTotal = categoryData.reduce((sum, c) => sum + c.total, 0);

                  return categoryData.length > 0 ? categoryData.map((cat) => {
                    const pct = grandTotal > 0 ? Math.round((cat.total / grandTotal) * 100) : 0;
                    const isExpanded = expandedCategory === cat.name;
                    return (
                      <Box key={cat.name} sx={{ borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}>
                        <Box
                          onClick={() => setExpandedCategory(isExpanded ? null : cat.name)}
                          sx={{ py: 1.5, px: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#fafafa' }, transition: 'background-color 0.15s' }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{
                              width: isMobile ? 40 : 48,
                              height: isMobile ? 40 : 48,
                              borderRadius: '50%',
                              backgroundColor: `${cat.color}18`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Typography sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>{cat.icon}</Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                <Typography variant={isMobile ? "body2" : "subtitle1"} fontWeight={600}>
                                  {cat.name}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant={isMobile ? "body2" : "subtitle1"} fontWeight={700}>
                                    KSh {cat.total.toLocaleString()}
                                  </Typography>
                                  {isExpanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
                                </Stack>
                              </Stack>
                              <Box sx={{ mt: 0.5 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: '#f0f0f0',
                                    '& .MuiLinearProgress-bar': { backgroundColor: cat.color, borderRadius: 3 }
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: cat.color, fontWeight: 600, mt: 0.25, display: 'inline-block' }}>
                                  {pct}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {cat.entityCount} {cat.entityCount === 1 ? 'entity' : 'entities'}
                                </Typography>
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                        <Collapse in={isExpanded} timeout="auto">
                          <Box sx={{ pl: isMobile ? 7 : 9, pr: 1, pb: 1.5 }}>
                            {cat.entities.map((entity, i) => (
                              <Stack
                                key={entity.name}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                  py: 0.75,
                                  px: 1,
                                  borderRadius: 1,
                                  backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'transparent',
                                }}
                              >
                                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1 }}>
                                  {entity.name}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                                  KSh {entity.total.toLocaleString()}
                                </Typography>
                              </Stack>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Tag entities above to see category spending breakdown
                    </Typography>
                  );
                })()}
              </Stack>

              {Object.keys(entityCategories).length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Category sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No categories tagged yet. Start by tagging entities above to see category breakdowns.
                  </Typography>
                </Box>
              )}
            </Paper>
            
            




            {/* Chart Section */}
             {granularChartData && granularChartData.length > 0 ? (
        <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" align="center" gutterBottom>
                   Total {viewMode === 'withdrawn' ? 'Withdrawn' : 'Paid In'}: {granularChartData.reduce((sum, day) => sum + (day.total_amount || 0), 0).toLocaleString()}
          </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
                  The chart below shows only the top 10 people/businesses by total amount. To see all transactions, use the table below.
          </Typography>

                <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
                  <ToggleButtonGroup
                    value={chartGranularity}
                    exclusive
                    onChange={(e, val) => { if (val) setChartGranularity(val); }}
                    size="small"
                  >
                    <ToggleButton value="daily">Daily</ToggleButton>
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                    <ToggleButton value="yearly">Yearly</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                <Stack 
                  direction="row" 
                  spacing={{ xs: 1, sm: 4 }} 
                  justifyContent="center" 
                  alignItems="center"
                  sx={{ mb: 2, flexWrap: 'wrap' }}
                >
                  <Typography variant="caption" color="success.main" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                    <Tooltip title="Average daily amount across all dates in the filtered data">
                      <span>
                        Daily (Avg.): <b>{analysisData.dayAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                      </span>
                    </Tooltip>
                  </Typography>
                  <Typography variant="caption" color="success.main" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                    <Tooltip title="Average weekly amount across all weeks in the filtered data">
                      <span>
                        Weekly (Avg.): <b>{analysisData.weekAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                      </span>
                    </Tooltip>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                    <Tooltip title="Average monthly amount across all months in the filtered data">
                      <span>
                        Monthly (Avg.): <b>{analysisData.monthAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                      </span>
                    </Tooltip>
                  </Typography>
                </Stack>
                                <Box sx={{ height: { xs: 250, sm: 500 } }}>
                  <Bar data={chartJsData} options={chartOptions} />
                </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" align="center" color="text.secondary">
            No chart data available
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            {analysisData.enhancedChartData ? 'Chart data is empty' : 'Chart data is undefined'}
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            Debug: allChartEntityNames length: {analysisData.allChartEntityNames?.length || 'undefined'}
          </Typography>
        </Paper>
      )}

      {/* Line Chart Section - Shows transaction count trends */}
      {filteredLineChartData && filteredLineChartData.length > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Transaction Count Trends
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Daily transaction counts for selected entities and search filters
          </Typography>
          <Box sx={{ height: { xs: 250, sm: 400 } }}>
            <Line data={chartJsLineData} options={chartJsLineOptions} />
          </Box>
        </Paper>
      )}

      

            {/* All People/Businesses Section */}
        <Paper sx={{ p: 3, mb: 4 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">All People/Businesses</Typography>
                <Badge badgeContent={analysisData.filteredTopEntities?.length || 0} color="primary">
                  <Typography variant="body2" color="text.secondary">
                    entities
                  </Typography>
                </Badge>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                All entities from your transactions, sorted by total amount. Use the search to filter or select specific entities.
              </Typography>
              
              <Autocomplete
                multiple
                freeSolo
                options={allEntityNames}
                value={selectedEntities}
                onChange={(e, newValue) => setSelectedEntities(newValue)}
                inputValue={search}
                onInputChange={(e, newValue) => setSearch(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search People/Business"
            variant="outlined"
            size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
                                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Button variant="outlined" onClick={handleExportExcel}>
                      Export to Excel
                    </Button>
                    <Button variant="outlined" onClick={handleDownloadChart}>
                      Download Chart
                    </Button>
                    {Object.keys(entityCategories).length > 0 && (
                      <Button 
                        variant="outlined" 
                        onClick={() => {
                          const categoryData = Object.entries(entityCategories).map(([entity, category]) => ({
                            Entity: entity,
                            Category: category,
                            Icon: getCategoryIcon(category),
                            Color: getCategoryColor(category),
                            'Transaction Amount': getEntityTotal(entity),
                            'Last Updated': new Date().toISOString()
                          }));
                          
                          // Add summary sheet
                          const summaryData = Object.entries(
                            Object.entries(entityCategories).reduce((acc, [entity, category]) => {
                              const entityTotal = getEntityTotal(entity);
                              if (!acc[category]) acc[category] = { count: 0, total: 0 };
                              acc[category].count += 1;
                              acc[category].total += entityTotal;
                              return acc;
                            }, {})
                          ).map(([category, stats]) => ({
                            Category: category,
                            'Entity Count': stats.count,
                            'Total Amount': stats.total,
                            'Average per Entity': Math.round(stats.total / stats.count)
                          }));
                          
                          const ws1 = XLSX.utils.json_to_sheet(categoryData);
                          const ws2 = XLSX.utils.json_to_sheet(summaryData);
                          const wb = XLSX.utils.book_new();
                          XLSX.utils.book_append_sheet(wb, ws1, 'Entity Categories');
                          XLSX.utils.book_append_sheet(wb, ws2, 'Category Summary');
                          XLSX.writeFile(wb, `Statement_Categories_${new Date().toISOString().slice(0, 10)}.xlsx`);
                        }}
                      >
                        Export Categories
                      </Button>
                    )}
                  </Stack>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                <List>
                  {analysisData.filteredTopEntities
                    .map((entity, idx) => {
                      const color = COLORS[idx % COLORS.length];
                      // Calculate percentage of total displayed entities
                      const totalSum = analysisData.filteredTopEntities.reduce((sum, e) => sum + e.total, 0);
                      const percent = totalSum > 0 ? (entity.total / totalSum) * 100 : 0;
                      const entityCategory = entityCategories[(entity.details || '').trim()];
                      
                      return (
                        <ListItem key={idx} divider sx={{ borderLeft: `8px solid ${color}`, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <span style={{ minWidth: 0, flex: 1 }}>{entity.details}</span>
                                  {entityCategory && !isMobile && (
                                    <Chip
                                      label={entityCategory}
                                      size="small"
                                      icon={<span>{getCategoryIcon(entityCategory)}</span>}
                                      sx={{
                                        backgroundColor: getCategoryColor(entityCategory),
                                        color: 'white',
                                        fontSize: '0.7rem'
                                      }}
                                    />
                                  )}
                                </Stack>
                              }
                              secondary={
                                <Stack direction="column" spacing={0.5}>
                                  <span>{`${entity.total.toLocaleString()} (${percent.toFixed(1)}%)`}</span>
                                  {entityCategory && isMobile && (
                                    <Chip
                                      label={entityCategory}
                                      size="small"
                                      icon={<span>{getCategoryIcon(entityCategory)}</span>}
                                      sx={{
                                        backgroundColor: getCategoryColor(entityCategory),
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        alignSelf: 'flex-start'
                                      }}
                                    />
                                  )}
                                </Stack>
                              }
                            />
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: isMobile ? 'flex-end' : 'center',
                            mt: isMobile ? 1 : 0,
                            minWidth: isMobile ? 'auto' : '120px'
                          }}>
                            <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
                              <Button
                                size={isMobile ? "small" : "small"}
                                startIcon={!isMobile ? <Edit /> : null}
                                onClick={() => handleCategoryEdit(entity.details)}
                                sx={{ 
                                  minWidth: 'auto', 
                                  px: isMobile ? 0.5 : 1,
                                  fontSize: isMobile ? '0.7rem' : '0.875rem'
                                }}
                              >
                                {isMobile ? (entityCategory ? 'Edit' : 'Tag') : (entityCategory ? 'Edit' : 'Tag')}
                              </Button>
                              {entityCategory && (
                                <Button
                                  size={isMobile ? "small" : "small"}
                                  color="error"
                                  onClick={() => handleCategoryRemove(entity.details)}
                                  sx={{ 
                                    minWidth: 'auto', 
                                    px: isMobile ? 0.5 : 1,
                                    fontSize: isMobile ? '0.7rem' : '0.875rem'
                                  }}
                                >
                                  {isMobile ? '×' : 'Remove'}
                                </Button>
                              )}
                            </Stack>
                          </Box>
                </ListItem>
                      );
                    })}
          </List>
              </Box>
      </Paper>



            {/* Transactions Table */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>Transactions</Typography>
              <div
                style={{
                  width: '100%',
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <DataGrid
                  rows={analysisData.filteredPreviewRows.map((row, idx) => {
                    const rowObj = { id: idx };
                    (filteredData && filteredData.columns ? filteredData.columns : result.columns).forEach((col, colIdx) => {
                      rowObj[col] = row[colIdx];
                    });
                    return rowObj;
                  })}
                  columns={columns}
                  pageSize={25}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  disableSelectionOnClick
                  sx={{
                    fontSize: isMobile ? '0.8rem' : '1rem',
                    '& .MuiDataGrid-cell': {
                      py: isMobile ? 0.5 : 1,
                      px: isMobile ? 0.5 : 2,
                    },
                  }}
                  style={{
                    minWidth: isMobile ? 800 : '100%',
                    height: isMobile ? 400 : 500,
                  }}
                />
              </div>
            </Paper>
          </>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h4" color="primary" gutterBottom>
              Welcome to Your Dashboard
                        </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Analyze your business or personal transactions with ease.
                        </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Upload your PDF statements above to see detailed analysis, charts, and insights.
                        </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ p: 3, textAlign: 'center', maxWidth: 200, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Charts & Analytics</Typography>
                <Typography variant="body2" color="text.secondary">
                  Visualize your transaction patterns with interactive charts
                        </Typography>
              </Box>
              <Box sx={{ p: 3, textAlign: 'center', maxWidth: 200, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Detailed Tables</Typography>
                <Typography variant="body2" color="text.secondary">
                  Browse all transactions with search and filter capabilities
                        </Typography>
              </Box>
              <Box sx={{ p: 3, textAlign: 'center', maxWidth: 200, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Export Data</Typography>
                <Typography variant="body2" color="text.secondary">
                  Export your analysis to Excel or download charts as images
                        </Typography>
              </Box>
            </Stack>
                </Paper>
              )}
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <MuiAlert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={deleteAllOpen} onClose={() => setDeleteAllOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete All Analyses?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete all your saved analyses and dashboard data. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllOpen(false)} disabled={deleteAllLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllAnalyses}
            variant="contained"
            color="error"
            disabled={deleteAllLoading}
            startIcon={deleteAllLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
          >
            {deleteAllLoading ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Editing Dialog */}
      <Dialog open={showCategoryDialog} onClose={handleCategoryCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Category color="primary" />
            <span>Tag Category for "{editingEntity}"</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Choose a predefined category or create a custom one:
            </Typography>
            
            {/* Predefined Categories */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Predefined Categories:
              </Typography>
              <Grid container spacing={1}>
                {activeCategories.map((category) => (
                  <Grid item xs={6} sm={4} key={category.name}>
                    <Card
                      variant={editingCategory === category.name ? "elevation" : "outlined"}
                      sx={{
                        cursor: 'pointer',
                        border: editingCategory === category.name ? `2px solid ${category.color}` : '1px solid #e0e0e0',
                        '&:hover': {
                          borderColor: category.color,
                          backgroundColor: `${category.color}10`
                        }
                      }}
                      onClick={() => setEditingCategory(category.name)}
                    >
                      <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ mb: 0.5 }}>
                          {category.icon}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {category.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Custom Category */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Custom Category:
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter custom category name"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && customCategory.trim()) {
                    setEditingCategory(customCategory.trim());
                  }
                }}
              />
              {customCategory.trim() && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setEditingCategory(customCategory.trim())}
                  sx={{ mt: 1 }}
                >
                  Use Custom Category
                </Button>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCategoryCancel} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleCategorySave}
            variant="contained"
            startIcon={<Save />}
            disabled={!editingCategory}
          >
            Save Category
          </Button>
        </DialogActions>
      </Dialog>
            </Box>
  );
}

export default Dashboard; 